import React, { useEffect, useMemo, useState } from "react";

const ENTITY_OPTIONS = ["VTPL", "RNS", "HUF", "TRS"];
const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const AUTH_TOKEN_KEY = "rajashwa_auth_token";

function apiUrl(path) {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path}`;
}

function getAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

async function apiFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (options.body && !(options.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(apiUrl(path), { ...options, headers });
  if (response.status === 401 && token) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
  return response;
}

const initialForm = {
  tenant_name: "",
  property_name: "",
  entity_name: "VTPL",
  advance_paid: "",
  advance_paid_date: "",
  exempted_amount: "",
  taxable_amount: "",
  gst: "",
  total_rent: "",
  tds: "",
  monthly_receivable: "",
  received: "",
  received_fy: "",
  received_date: "",
  dues_month: "",
  remarks: ""
};

function parseAmountInput(value) {
  if (value === null || value === undefined || value === "") return NaN;
  const text = String(value)
    .trim()
    .replace(/[₹,\s]/g, "")
    .replace(/[^\d.-]/g, "");
  if (!text || text === "-" || text === ".") return NaN;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function amountValueOrZero(value) {
  const parsed = parseAmountInput(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function rentAmountsFromForm(formData) {
  const exempted = parseAmountInput(formData.exempted_amount);
  const taxable = parseAmountInput(formData.taxable_amount);
  return {
    exempted_amount: !Number.isNaN(exempted) && exempted > 0 ? exempted : undefined,
    taxable_amount: !Number.isNaN(taxable) && taxable > 0 ? taxable : undefined
  };
}

function computeRentFields(exemptedAmount, taxableAmount) {
  const exemptedText = String(exemptedAmount ?? "").trim();
  const taxableText = String(taxableAmount ?? "").trim();

  if (exemptedText) {
    const exempted = parseAmountInput(exemptedText);
    if (!Number.isNaN(exempted) && exempted > 0) {
      return {
        rent_type: "exempted",
        exempted_amount: exempted,
        taxable_amount: null,
        gst: null,
        total_rent: exempted,
        tds: null,
        monthly_receivable: exempted
      };
    }
  }

  if (taxableText) {
    const taxable = parseAmountInput(taxableText);
    if (!Number.isNaN(taxable) && taxable > 0) {
      const gst = Math.round(taxable * 0.18 * 100) / 100;
      const total_rent = Math.round((taxable + gst) * 100) / 100;
      const tds = Math.round(taxable * 0.10 * 100) / 100;
      const monthly_receivable = Math.round((total_rent - tds) * 100) / 100;
      return {
        rent_type: "taxable",
        exempted_amount: null,
        taxable_amount: taxable,
        gst,
        total_rent,
        tds,
        monthly_receivable
      };
    }
  }

  return null;
}

function formatRentCell(value, inrFormatter) {
  if (value === null || value === undefined || value === "") return "NA";
  return inrFormatter.format(Number(value || 0));
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function buildDueMonthOptions() {
  const options = [];
  const now = new Date();
  for (let offset = -24; offset <= 6; offset += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    options.push(`${MONTH_SHORT[date.getMonth()]}-${date.getFullYear()}`);
  }
  return options;
}

const PASS_SHEET_DUE_MONTH_OPTIONS = buildDueMonthOptions();

function getDueMonthPickerOptions(proposedMonth) {
  const recent = PASS_SHEET_DUE_MONTH_OPTIONS.slice(-15);
  const suggested = parseDueMonthSelection(proposedMonth);
  return [...new Set([...suggested, ...recent])];
}

function buildFyMonthPickerOptions(fyStartMonth = 4) {
  const now = new Date();
  const fyStartYear = now.getMonth() + 1 >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  let year = fyStartYear;
  let month = fyStartMonth - 1;
  while (year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth())) {
    options.push(`${MONTH_SHORT[month]}-${year}`);
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return options;
}

const FY_MONTH_PICKER_OPTIONS = buildFyMonthPickerOptions();

function getDefaultMonthReceiptDraft() {
  const now = new Date();
  return {
    year_month: `${MONTH_SHORT[now.getMonth()]}-${now.getFullYear()}`,
    received_full: true,
    received_amount: "",
    received_date: now.toISOString().slice(0, 10)
  };
}

function getMonthlyBalanceClass(balance) {
  return Math.abs(Number(balance || 0)) > 99 ? "monthly-balance-overdue" : "";
}

function getTotalsTenantCurrentClass(balance) {
  return Number(balance) <= 0 ? "totals-tenant-current" : "";
}

function getFinancialYearMonthCount(fyStartMonth = 4) {
  const now = new Date();
  const fyStartYear = now.getMonth() + 1 >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const fyStartIndex = fyStartYear * 12 + (fyStartMonth - 1);
  const currentIndex = now.getFullYear() * 12 + now.getMonth();
  return Math.max(0, currentIndex - fyStartIndex + 1);
}

function getFinancialYearRangeLabel(fyStartMonth = 4) {
  const now = new Date();
  const startYear = now.getMonth() + 1 >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const startMonthLabel = MONTH_SHORT[fyStartMonth - 1];
  return `${startMonthLabel}-${startYear} to ${MONTH_SHORT[now.getMonth()]}-${now.getFullYear()}`;
}

function currentFiscalYear() {
  const now = new Date();
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
}

function resolveTenantFyStartMonth(tenant) {
  const entity = (tenant?.entity_name || "VTPL").trim();
  const property = (tenant?.property_name || "").trim();
  const name = (tenant?.tenant_name || "").trim();
  if (entity === "VTPL" && property === "Bloomfields" && name === "RR Roadways") {
    return currentFiscalYear() === 2026 ? 6 : 4;
  }
  return tenant?.fy_start_month || 4;
}

function getTenantFyMonthCount(tenant) {
  const fyStartMonth = resolveTenantFyStartMonth(tenant);
  return tenant?.fy_month_count || getFinancialYearMonthCount(fyStartMonth);
}

function getFyMonthColumns() {
  const now = new Date();
  const fiscalYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = [];
  let year = fiscalYear;
  let month = 3;
  while (year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth())) {
    months.push(`${MONTH_SHORT[month]}-${year}`);
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return { fiscalYear, months };
}

function nextMonthHighlight(current) {
  if (!current) return "correct";
  if (current === "correct") return "incorrect";
  return null;
}

function monthHighlightClass(highlight) {
  if (highlight === "correct") return "cs-total-correct";
  if (highlight === "incorrect") return "cs-total-incorrect";
  return "";
}

function getTenantMonthlyReceivable(tenant) {
  const monthly = Number(tenant.monthly_receivable || 0);
  if (monthly > 0) return monthly;
  const computed = computeRentFields(tenant.exempted_amount, tenant.taxable_amount);
  return computed?.monthly_receivable || 0;
}

function sumTenantFyReceived(tenant) {
  const receipts = tenant.monthly_receipts;
  if (!Array.isArray(receipts) || receipts.length === 0) return null;

  const fyStartMonth = resolveTenantFyStartMonth(tenant);
  const now = new Date();
  const fyStartYear = now.getMonth() + 1 >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const fyStartIndex = fyStartYear * 12 + (fyStartMonth - 1);
  const currentIndex = now.getFullYear() * 12 + now.getMonth();

  const total = receipts.reduce((sum, receipt) => {
    const key = receipt.year_month;
    if (!key) return sum;
    const [year, month] = key.split("-").map(Number);
    const monthIndex = year * 12 + (month - 1);
    if (monthIndex < fyStartIndex || monthIndex > currentIndex) return sum;
    return sum + Number(receipt.received_amount || 0);
  }, 0);

  return Math.round(total * 100) / 100;
}

function sanitizeTenantDuesMonth(tenant) {
  const raw = (tenant?.dues_month || "").trim();
  if (!raw) return "";

  const fyStartMonth = resolveTenantFyStartMonth(tenant);
  const months = parseDueMonthSelection(raw).filter((label) => {
    const match = String(label).match(/^([A-Za-z]{3})-(\d{4})$/);
    if (!match) return true;
    const monthIndex = MONTH_SHORT.indexOf(match[1]);
    const year = Number(match[2]);
    if (monthIndex < 0) return true;
    const now = new Date();
    const fyStartYear =
      now.getMonth() + 1 >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
    const startIndex = fyStartYear * 12 + (fyStartMonth - 1);
    return year * 12 + monthIndex >= startIndex;
  });

  return formatDueMonthSelection(months);
}

function getTenantUnpaidLabels(tenant) {
  if (Array.isArray(tenant?.computed_unpaid_labels) && tenant.computed_unpaid_labels.length) {
    return tenant.computed_unpaid_labels;
  }
  const parsed = parseDueMonthSelection(
    tenant?.dues_month || tenant?.computed_dues_month || sanitizeTenantDuesMonth(tenant)
  );
  return parsed;
}

function formatUnpaidMonthDisplay(tenant) {
  const labels = getTenantUnpaidLabels(tenant);
  if (!labels.length) return "-";
  return labels.join(", ");
}

function buildFyTenantTotals(tenant) {
  const monthly = getTenantMonthlyReceivable(tenant);
  const fyMonthCount = getTenantFyMonthCount(tenant);
  const computedFy = Math.round(monthly * fyMonthCount * 100) / 100;
  const fyReceivable = Number(tenant.fy_receivable) || computedFy;
  const receiptFyReceived = sumTenantFyReceived(tenant);
  const receivedFy =
    receiptFyReceived !== null
      ? receiptFyReceived
      : Number(tenant.received_fy ?? tenant.received ?? 0);
  const balance = Math.round((fyReceivable - receivedFy) * 100) / 100;
  const unpaidLabels = getTenantUnpaidLabels(tenant);
  return {
    tenantId: tenant.id,
    tenantName: tenant.tenant_name,
    propertyName: tenant.property_name,
    monthlyReceivable: monthly,
    fyReceivable,
    received: receivedFy,
    balance,
    duesMonth: unpaidLabels.length ? unpaidLabels.join(", ") : "-",
    unpaidLabels,
    remarks: tenant.remarks || "",
    fyStartMonth: resolveTenantFyStartMonth(tenant)
  };
}

const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  sept: 8,
  oct: 9,
  nov: 10,
  dec: 11
};

function parseDueMonthEntries(value) {
  if (!value) return [];
  const text = String(value).toLowerCase();
  const matches = [...text.matchAll(/([a-z]{3,9})(?:\s*&\s*([a-z]{3,9}))?\s*[-/ ]\s*(\d{4})/g)];
  const entries = [];

  matches.forEach((match) => {
    const year = Number(match[3]);
    const firstMonth = MONTH_INDEX[match[1]?.slice(0, 4).replace(".", "")];
    const secondMonth = match[2] ? MONTH_INDEX[match[2]?.slice(0, 4).replace(".", "")] : undefined;

    if (firstMonth !== undefined) {
      entries.push({ month: firstMonth, year });
    }
    if (secondMonth !== undefined) {
      entries.push({ month: secondMonth, year });
    }
  });

  return entries;
}

function parseDueMonthSelection(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  const entries = parseDueMonthEntries(value);
  if (entries.length > 0) {
    return entries.map((entry) => `${MONTH_SHORT[entry.month]}-${entry.year}`);
  }
  const single = String(value).trim().match(/^([A-Za-z]{3})-(\d{4})$/);
  return single ? [single[0]] : [];
}

function formatDueMonthSelection(months) {
  const unique = [...new Set((months || []).filter(Boolean))];
  if (unique.length === 0) return "";
  if (unique.length === 1) return unique[0];

  const parsed = unique
    .map((month) => {
      const match = month.match(/^([A-Za-z]{3})-(\d{4})$/);
      return match ? { label: match[1], year: match[2] } : null;
    })
    .filter(Boolean);

  const years = [...new Set(parsed.map((entry) => entry.year))];
  if (years.length === 1) {
    return `${parsed.map((entry) => entry.label).join(" & ")}-${years[0]}`;
  }
  return unique.join(" & ");
}

function getDueClass(value) {
  const parsedEntries = parseDueMonthEntries(value);

  if (parsedEntries.length > 0) {
    const now = new Date();
    const currentMonthNumber = now.getFullYear() * 12 + now.getMonth();
    const isBeforeCurrentMonth = parsedEntries.some(
      (entry) => entry.year * 12 + entry.month < currentMonthNumber
    );
    if (isBeforeCurrentMonth) return "due-overdue";
    return "";
  }

  return "";
}

function formatDisplayDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function toGoogleMapsUrl(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
}

function GoogleMapLink({ value }) {
  const url = toGoogleMapsUrl(value);
  if (!url) return <span>-</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="map-link">
      Open in Google Maps
    </a>
  );
}

function formatDisplayDateList(value) {
  if (!value) return "-";
  const parts = String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (parts.length === 0) return "-";

  return parts
    .map((item) => {
      const formatted = formatDisplayDate(item);
      return formatted === "-" ? item : formatted;
    })
    .join(", ");
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(max-width: 720px)").matches) {
      setIsSidebarCollapsed(true);
    }
  }, []);
  const [activeEntity, setActiveEntity] = useState("");
  const [activeSection, setActiveSection] = useState("complete-summary");
  const [activeForm, setActiveForm] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [propertyDetailPropertyId, setPropertyDetailPropertyId] = useState("");
  const [propertyDetailForm, setPropertyDetailForm] = useState({
    property_name: "",
    physical_address: "",
    google_map_location: "",
    building_type: "",
    sq_ft: "",
    other_comments: ""
  });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [userEmail, setUserEmail] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [loginStep, setLoginStep] = useState("credentials");
  const [totpCode, setTotpCode] = useState("");
  const [totpSetup, setTotpSetup] = useState({ qr_data_url: "", manual_secret: "" });
  const [loginError, setLoginError] = useState("");
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newPropertyName, setNewPropertyName] = useState("");
  const [formData, setFormData] = useState(initialForm);
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [generalMessage, setGeneralMessage] = useState("");
  const [uploadInfo, setUploadInfo] = useState({
    tenant_id: "",
    property_name: "",
    document_name: "",
    file: null
  });
  const [tenantDetailForm, setTenantDetailForm] = useState({
    tenant_id: "",
    tenant_email: "",
    tenant_contact_no: "",
    tenant_address: "",
    advance_paid: "",
    advance_paid_date: ""
  });
  const [openAiKeyInput, setOpenAiKeyInput] = useState("");
  const [openAiConfigured, setOpenAiConfigured] = useState(false);
  const [passSheetFile, setPassSheetFile] = useState(null);
  const [passSheetScanning, setPassSheetScanning] = useState(false);
  const [passSheetResult, setPassSheetResult] = useState(null);
  const [passSheetApprovals, setPassSheetApprovals] = useState({});
  const [passSheetDueMonths, setPassSheetDueMonths] = useState({});
  const [monthReceiptDrafts, setMonthReceiptDrafts] = useState({});
  const [monthReceiptSuccess, setMonthReceiptSuccess] = useState({});
  const [rentEditOnly, setRentEditOnly] = useState(false);
  const [totalsEditTenantId, setTotalsEditTenantId] = useState(null);
  const [totalsEditForm, setTotalsEditForm] = useState({
    receivable: "",
    received_fy: "",
    dues_month: "",
    unpaidMonthLabels: [],
    remarks: ""
  });
  const [totalsEditLedger, setTotalsEditLedger] = useState(null);
  const [financialsModal, setFinancialsModal] = useState(null);
  const [passSheetRules, setPassSheetRules] = useState([]);
  const [passSheetRuleDraft, setPassSheetRuleDraft] = useState(null);
  const [passSheetRulesLoading, setPassSheetRulesLoading] = useState(false);
  const [completeSummary, setCompleteSummary] = useState({ fiscal_year: null, months: [], rows: [] });
  const [csEditTenantId, setCsEditTenantId] = useState(null);
  const [csEditDraft, setCsEditDraft] = useState(null);

  const entityProperties = useMemo(
    () => properties.filter((item) => item.entity_name === activeEntity),
    [properties, activeEntity]
  );

  const groupedPropertyData = useMemo(
    () =>
      entityProperties.map((property) => ({
        ...property,
        tenants: tenants.filter((tenant) => tenant.property_name === property.property_name)
      })),
    [entityProperties, tenants]
  );


  const fyMonthColumns = useMemo(() => getFyMonthColumns(), []);

  const fyTotalsByProperty = useMemo(() => {
    return groupedPropertyData.map((property) => {
      const tenantRows = property.tenants.map((tenant) => buildFyTenantTotals(tenant));
      const propertyTotals = tenantRows.reduce(
        (acc, row) => ({
          fyReceivable: acc.fyReceivable + row.fyReceivable,
          received: acc.received + row.received,
          balance: acc.balance + row.balance
        }),
        { fyReceivable: 0, received: 0, balance: 0 }
      );
      return {
        propertyId: property.id,
        propertyName: property.property_name,
        tenants: tenantRows,
        totals: propertyTotals
      };
    });
  }, [groupedPropertyData]);

  const entityTotals = useMemo(() => {
    const totals = fyTotalsByProperty.reduce(
      (acc, property) => ({
        totalReceivable: acc.totalReceivable + property.totals.fyReceivable,
        totalReceived: acc.totalReceived + property.totals.received,
        totalBalance: acc.totalBalance + property.totals.balance
      }),
      { totalReceivable: 0, totalReceived: 0, totalBalance: 0 }
    );
    return totals;
  }, [fyTotalsByProperty]);

  const documentsByProperty = useMemo(() => {
    const grouped = {};
    documents.forEach((doc) => {
      const key = doc.property_name || "Unassigned";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    });
    return grouped;
  }, [documents]);

  const loadProperties = async () => {
    const response = await apiFetch("/properties");
    const data = await response.json();
    setProperties(data);
  };

  const loadTenants = async (entity = activeEntity) => {
    const response = await apiFetch(`/tenants?entity_name=${encodeURIComponent(entity)}`);
    const data = await response.json();
    setTenants(data);
  };

  const loadDocuments = async (entity = activeEntity) => {
    const response = await apiFetch(`/documents?entity_name=${encodeURIComponent(entity)}`);
    const data = await response.json();
    setDocuments(data);
  };

  const loadOpenAiKeyStatus = async () => {
    const response = await apiFetch("/settings/openai-api-key");
    const data = await response.json();
    setOpenAiConfigured(Boolean(data.configured));
  };

  const loadCompleteSummary = async () => {
    const response = await apiFetch("/complete-summary");
    const data = await response.json();
    if (!response.ok) {
      setGeneralMessage(data.message || "Unable to load complete summary");
      return;
    }
    setCompleteSummary(data);
  };

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const token = getAuthToken();
      if (!token) return;

      const response = await apiFetch("/auth/me");
      if (!response.ok) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        return;
      }
      if (cancelled) return;

      const data = await response.json();
      setUserEmail(data.email || "");
      setIsLoggedIn(true);
      await loadProperties();
      setTenants([]);
      setDocuments([]);
      await loadOpenAiKeyStatus();
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn || activeSection !== "complete-summary") return;
    loadCompleteSummary();
  }, [isLoggedIn, activeSection]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const syncSidebar = () => {
      if (mq.matches) {
        setIsSidebarCollapsed(true);
      }
    };
    syncSidebar();
    mq.addEventListener("change", syncSidebar);
    return () => mq.removeEventListener("change", syncSidebar);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || activeSection !== "pass-sheet" || !activeEntity) return;
    loadPassSheetRules(activeEntity);
  }, [isLoggedIn, activeSection, activeEntity]);

  const saveOpenAiKey = async (event) => {
    event.preventDefault();
    if (!openAiKeyInput.trim()) {
      setGeneralMessage("Enter an OpenAI API key");
      return;
    }
    const response = await apiFetch("/settings/openai-api-key", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ api_key: openAiKeyInput.trim() })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save API key");
      return;
    }
    setOpenAiConfigured(true);
    setOpenAiKeyInput("");
    setGeneralMessage("OpenAI API key saved");
  };

  const loadPassSheetRules = async (entity = activeEntity) => {
    if (!entity) return;
    setPassSheetRulesLoading(true);
    const response = await apiFetch(`/pass-sheet/rules?entity_name=${encodeURIComponent(entity)}`);
    const data = await response.json();
    setPassSheetRulesLoading(false);
    if (response.ok) {
      setPassSheetRules(data.rules || []);
    } else {
      setGeneralMessage(data.message || "Unable to load pass sheet rules");
    }
  };

  const openPassSheetRuleEditor = (rule, index) => {
    setPassSheetRuleDraft({
      index,
      label: rule?.label || "",
      property_name: rule?.property_name || "",
      narration_keywords: (rule?.narration_keywords || []).join(", "),
      tenant_name_patterns: (rule?.tenant_name_patterns || []).join(", ")
    });
  };

  const savePassSheetRuleDraft = async () => {
    if (!passSheetRuleDraft || !activeEntity) return;
    const payload = {
      entity_name: activeEntity,
      label: passSheetRuleDraft.label,
      property_name: passSheetRuleDraft.property_name,
      narration_keywords: passSheetRuleDraft.narration_keywords
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      tenant_name_patterns: passSheetRuleDraft.tenant_name_patterns
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    };
    const isNew = passSheetRuleDraft.index === null || passSheetRuleDraft.index === undefined;
    const url = isNew
      ? "/pass-sheet/rules"
      : `/pass-sheet/rules/${passSheetRuleDraft.index}`;
    const response = await apiFetch(url, {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save rule");
      return;
    }
    setPassSheetRules(result.rules || []);
    setPassSheetRuleDraft(null);
    setGeneralMessage(isNew ? "Rule added" : "Rule updated");
  };

  const deletePassSheetRule = async (index) => {
    if (!window.confirm("Delete this matching rule?")) return;
    const response = await apiFetch(
      `/pass-sheet/rules/${index}?entity_name=${encodeURIComponent(activeEntity)}`,
      { method: "DELETE" }
    );
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to delete rule");
      return;
    }
    setPassSheetRules(result.rules || []);
    setGeneralMessage("Rule deleted");
  };

  const resetPassSheetRulesToDefault = async () => {
    if (!window.confirm("Reset all rules for this entity to defaults?")) return;
    const response = await apiFetch("/pass-sheet/rules/reset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity_name: activeEntity })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to reset rules");
      return;
    }
    setPassSheetRules(result.rules || []);
    setGeneralMessage("Rules reset to defaults");
  };

  const handlePassSheetFile = (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setPassSheetFile(file);
    setPassSheetResult(null);
    setPassSheetApprovals({});
    setPassSheetDueMonths({});
    setMonthReceiptSuccess({});
    setMonthReceiptDrafts({});
  };

  const handlePassSheetScan = async (event) => {
    event.preventDefault();
    if (!passSheetFile) {
      setGeneralMessage("Select an Excel pass sheet file first");
      return;
    }
    setPassSheetScanning(true);
    setGeneralMessage("");
    const body = new FormData();
    body.append("file", passSheetFile);
    body.append("entity_name", activeEntity);
    body.append("use_ai", "false");
    try {
      const response = await apiFetch("/pass-sheet/scan", { method: "POST", body });
      const result = await response.json();
      if (!response.ok) {
        setGeneralMessage(result.message || "Pass sheet scan failed");
        return;
      }
      setPassSheetResult(result);
      const initialApprovals = {};
      const initialDueMonths = {};
      (result.matches || []).forEach((row) => {
        initialDueMonths[row.match_id] = parseDueMonthSelection(row.proposed_dues_month);
        if (row.tenant_id && row.tenant_found) {
          initialApprovals[row.match_id] = true;
        }
      });
      setPassSheetApprovals(initialApprovals);
      setPassSheetDueMonths(initialDueMonths);
      setGeneralMessage(
        `Found ${result.rent_payments_found || 0} expected tenant payment rule(s) in pass sheet`
      );
    } catch {
      setGeneralMessage("Pass sheet scan failed — check backend is running");
    } finally {
      setPassSheetScanning(false);
    }
  };

  const togglePassSheetApproval = (matchId) => {
    setPassSheetApprovals((prev) => ({ ...prev, [matchId]: !prev[matchId] }));
  };

  const togglePassSheetDueMonth = (matchId, month, checked) => {
    setPassSheetDueMonths((prev) => {
      const current = prev[matchId] || [];
      const next = checked ? [...new Set([...current, month])] : current.filter((item) => item !== month);
      return { ...prev, [matchId]: next };
    });
  };

  const handlePassSheetApply = async () => {
    if (!passSheetResult) return;
    const approvals = (passSheetResult.matches || [])
      .filter((row) => passSheetApprovals[row.match_id] && row.tenant_id)
      .map((row) => ({
        tenant_id: row.tenant_id,
        deposit_amount: row.deposit_amount,
        dues_month: formatDueMonthSelection(
          passSheetDueMonths[row.match_id]?.length
            ? passSheetDueMonths[row.match_id]
            : parseDueMonthSelection(row.proposed_dues_month)
        ),
        approved: true
      }));
    const unpaidTenants = (passSheetResult.missing_tenants || [])
      .filter((item) => item.tenant_id)
      .map((item) => ({ tenant_id: item.tenant_id }));

    if (approvals.length === 0 && unpaidTenants.length === 0) {
      setGeneralMessage("Select at least one payment to approve, or ensure missing tenants exist in the database");
      return;
    }
    if (approvals.some((item) => !item.dues_month)) {
      setGeneralMessage("Select at least one due month for each approved payment");
      return;
    }
    const confirmParts = [];
    if (approvals.length > 0) confirmParts.push(`${approvals.length} rent payment(s)`);
    if (unpaidTenants.length > 0) confirmParts.push(`${unpaidTenants.length} property(ies) marked as rent not received`);
    if (!window.confirm(`Apply ${confirmParts.join(" and ")}?`)) return;

    const response = await apiFetch("/pass-sheet/apply", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        entity_name: activeEntity,
        approvals,
        unpaid_tenants: unpaidTenants
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to apply updates");
      return;
    }
    setGeneralMessage(result.message || "Pass sheet updates applied");
    setPassSheetResult(null);
    setPassSheetApprovals({});
    setPassSheetDueMonths({});
    setMonthReceiptSuccess({});
    setMonthReceiptDrafts({});
    setPassSheetFile(null);
    const fileInput = document.getElementById("pass-sheet-file");
    if (fileInput) fileInput.value = "";
    await loadTenants();
  };

  const finishLogin = async (token) => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    setUserEmail(loginForm.email || "");
    setIsLoggedIn(true);
    setLoginStep("credentials");
    setTotpCode("");
    setTotpSetup({ qr_data_url: "", manual_secret: "" });
    await loadProperties();
    setTenants([]);
    setDocuments([]);
    await loadOpenAiKeyStatus();
  };

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    const response = await fetch(apiUrl("/auth/login"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(loginForm)
    });
    const result = await response.json();
    if (!response.ok) {
      setLoginError(result.message || "Login failed");
      return;
    }

    if (result.step === "setup") {
      setTotpSetup({
        qr_data_url: result.qr_data_url || "",
        manual_secret: result.manual_secret || ""
      });
      setLoginStep("setup");
      return;
    }

    setLoginStep("totp");
  };

  const handleVerifyTotp = async (event) => {
    event.preventDefault();
    setLoginError("");
    const response = await fetch(apiUrl("/auth/verify-totp"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: loginForm.email,
        password: loginForm.password,
        code: totpCode
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setLoginError(result.message || "Invalid authenticator code");
      return;
    }
    await finishLogin(result.token);
  };

  const handleBackToCredentials = () => {
    setLoginStep("credentials");
    setTotpCode("");
    setTotpSetup({ qr_data_url: "", manual_secret: "" });
    setLoginError("");
  };

  const resetTenantForm = () => {
    setFormData({ ...initialForm, entity_name: activeEntity, property_name: "" });
    setEditingTenantId(null);
    setRentEditOnly(false);
    setActiveForm("");
  };

  const closeActiveForm = () => {
    if (activeForm === "add-tenant") {
      setFormData({ ...initialForm, entity_name: activeEntity, property_name: "" });
      setEditingTenantId(null);
      setRentEditOnly(false);
    }
    if (activeForm === "upload-docs") {
      setUploadInfo({ tenant_id: "", property_name: "", document_name: "", file: null });
      const fileInput = document.getElementById("doc-file");
      if (fileInput) fileInput.value = "";
    }
    if (activeForm === "add-property") {
      setNewPropertyName("");
    }
    if (activeForm === "tenant-details") {
      setTenantDetailForm({
        tenant_id: "",
        tenant_email: "",
        tenant_contact_no: "",
        tenant_address: "",
        advance_paid: "",
        advance_paid_date: ""
      });
    }
    setActiveForm("");
  };

  const openAddTenantForm = () => {
    if (!activeEntity) {
      setGeneralMessage("Select an entity from the sidebar first");
      return;
    }
    setFormData({ ...initialForm, entity_name: activeEntity, property_name: "" });
    setEditingTenantId(null);
    setRentEditOnly(false);
    setActiveForm("add-tenant");
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "exempted_amount" && value) {
        next.taxable_amount = "";
      }
      if (name === "taxable_amount" && value) {
        next.exempted_amount = "";
      }
      if (name === "exempted_amount" || name === "taxable_amount") {
        const preview = computeRentFields(
          name === "exempted_amount" ? value : next.exempted_amount,
          name === "taxable_amount" ? value : next.taxable_amount
        );
        if (preview) {
          next.gst = preview.gst != null ? String(preview.gst) : "";
          next.total_rent = preview.total_rent != null ? String(preview.total_rent) : "";
          next.tds = preview.tds != null ? String(preview.tds) : "";
          next.monthly_receivable = String(preview.monthly_receivable);
        }
      }
      return next;
    });
  };

  const formRentPreview = useMemo(
    () => computeRentFields(formData.exempted_amount, formData.taxable_amount),
    [formData.exempted_amount, formData.taxable_amount]
  );

  const effectiveFormMonthlyReceivable = useMemo(() => {
    const override = parseAmountInput(formData.monthly_receivable);
    if (!Number.isNaN(override) && formData.monthly_receivable !== "") return override;
    return formRentPreview?.monthly_receivable || 0;
  }, [formData.monthly_receivable, formRentPreview]);

  const editingTenant = useMemo(
    () => (editingTenantId ? tenants.find((item) => item.id === editingTenantId) : null),
    [editingTenantId, tenants]
  );

  const editFormBalance = useMemo(() => {
    const receivable = Number(editingTenant?.receivable || effectiveFormMonthlyReceivable || 0);
    const received = amountValueOrZero(formData.received);
    return receivable - received;
  }, [editingTenant, formData.received, effectiveFormMonthlyReceivable]);

  const formTenantFyStart = useMemo(
    () =>
      resolveTenantFyStartMonth({
        entity_name: activeEntity,
        property_name: formData.property_name,
        tenant_name: formData.tenant_name
      }),
    [activeEntity, formData.property_name, formData.tenant_name]
  );

  const editFormFyBalance = useMemo(() => {
    const fyMonthCount = editingTenant
      ? getTenantFyMonthCount(editingTenant)
      : getFinancialYearMonthCount(formTenantFyStart);
    const fyReceivable = Number(
      editingTenant?.fy_receivable ||
        effectiveFormMonthlyReceivable * fyMonthCount
    );
    const receivedFy = amountValueOrZero(formData.received_fy);
    return fyReceivable - receivedFy;
  }, [editingTenant, formData.received_fy, effectiveFormMonthlyReceivable, formTenantFyStart]);

  const handleTenantSubmit = async (event) => {
    event.preventDefault();
    setGeneralMessage("");
    const method = editingTenantId ? "PUT" : "POST";
    const url = editingTenantId ? `/tenants/${editingTenantId}` : "/tenants";
    const existingTenant = editingTenantId
      ? tenants.find((item) => item.id === editingTenantId)
      : null;

    const rentAmounts = rentAmountsFromForm(formData);

    if (!editingTenantId && !rentAmounts.exempted_amount && !rentAmounts.taxable_amount) {
      setGeneralMessage("Enter a valid Exempted or Taxable monthly rent amount");
      return;
    }

    const rentOverrides = {};
    if (formData.gst !== "") {
      rentOverrides.gst = amountValueOrZero(formData.gst);
    }
    if (formData.total_rent !== "") {
      rentOverrides.total_rent = amountValueOrZero(formData.total_rent);
    }
    if (formData.monthly_receivable !== "") {
      rentOverrides.monthly_receivable = amountValueOrZero(formData.monthly_receivable);
    }
    if (formData.tds !== "") {
      rentOverrides.tds = amountValueOrZero(formData.tds);
    }

    const basePayload = {
      ...rentAmounts,
      ...rentOverrides,
      received:
        rentEditOnly && existingTenant
          ? amountValueOrZero(existingTenant.received)
          : amountValueOrZero(formData.received),
      received_fy:
        rentEditOnly && existingTenant
          ? amountValueOrZero(existingTenant.received_fy ?? existingTenant.received)
          : amountValueOrZero(formData.received_fy),
      received_date:
        rentEditOnly && existingTenant ? existingTenant.received_date || null : formData.received_date || null,
      dues_month: formData.dues_month ?? "",
      remarks: rentEditOnly && existingTenant ? existingTenant.remarks : formData.remarks
    };

    const payload = editingTenantId && existingTenant
      ? {
          tenant_name: existingTenant.tenant_name,
          property_name: existingTenant.property_name,
          entity_name: activeEntity,
          advance_paid: existingTenant.advance_paid,
          advance_paid_date: existingTenant.advance_paid_date,
          tenant_email: existingTenant.tenant_email,
          tenant_contact_no: existingTenant.tenant_contact_no,
          tenant_address: existingTenant.tenant_address,
          ...basePayload
        }
      : {
          ...formData,
          entity_name: activeEntity,
          advance_paid: 0,
          advance_paid_date: null,
          ...basePayload
        };
    const response = await apiFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save tenant");
      return;
    }
    setGeneralMessage(editingTenantId ? "Tenant updated" : "Tenant added");
    resetTenantForm();
    await loadTenants();
    await loadCompleteSummary();
  };

  const cancelTotalsEdit = () => {
    setTotalsEditTenantId(null);
    setTotalsEditLedger(null);
    setTotalsEditForm({
      receivable: "",
      received_fy: "",
      dues_month: "",
      unpaidMonthLabels: [],
      remarks: ""
    });
  };

  const startTotalsEdit = async (tenant) => {
    setTotalsEditTenantId(tenant.id);
    const monthly = getTenantMonthlyReceivable(tenant);
    const computedFy =
      Math.round(monthly * getTenantFyMonthCount(tenant) * 100) / 100;
    const response = await apiFetch(`/tenants/${tenant.id}/rent-ledger`);
    const ledger = response.ok ? await response.json() : null;
    setTotalsEditLedger(ledger);
    const unpaidMonthLabels = ledger?.computed_unpaid_labels?.length
      ? ledger.computed_unpaid_labels
      : getTenantUnpaidLabels(tenant);
    setTotalsEditForm({
      receivable: tenant.fy_receivable ?? computedFy ?? tenant.receivable ?? "",
      received_fy: tenant.received_fy ?? tenant.received ?? "",
      dues_month: formatDueMonthSelection(unpaidMonthLabels),
      unpaidMonthLabels,
      remarks: tenant.remarks ?? ""
    });
  };

  const toggleTotalsUnpaidMonth = (label) => {
    setTotalsEditForm((prev) => {
      const set = new Set(prev.unpaidMonthLabels || []);
      if (set.has(label)) set.delete(label);
      else set.add(label);
      const unpaidMonthLabels = [...set].sort();
      return {
        ...prev,
        unpaidMonthLabels,
        dues_month: formatDueMonthSelection(unpaidMonthLabels)
      };
    });
  };

  const handleTotalsEditInput = (event) => {
    const { name, value } = event.target;
    setTotalsEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveTotalsEdit = async (tenantId) => {
    const tenant = tenants.find((item) => String(item.id) === String(tenantId));
    if (!tenant) {
      setGeneralMessage("Tenant not found");
      return;
    }
    const receivable = amountValueOrZero(totalsEditForm.receivable);
    const receivedFy = amountValueOrZero(totalsEditForm.received_fy);
    const response = await apiFetch(`/tenants/${tenant.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_name: tenant.tenant_name,
        property_name: tenant.property_name,
        entity_name: tenant.entity_name,
        exempted_amount: tenant.exempted_amount,
        taxable_amount: tenant.taxable_amount,
        received: tenant.received,
        received_fy: receivedFy,
        received_date: tenant.received_date,
        dues_month: formatDueMonthSelection(totalsEditForm.unpaidMonthLabels),
        receivable,
        remarks: totalsEditForm.remarks,
        advance_paid: tenant.advance_paid,
        advance_paid_date: tenant.advance_paid_date,
        tenant_email: tenant.tenant_email,
        tenant_contact_no: tenant.tenant_contact_no,
        tenant_address: tenant.tenant_address
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save totals");
      return;
    }
    setGeneralMessage(`Updated totals for ${tenant.tenant_name}`);
    cancelTotalsEdit();
    await loadTenants();
  };

  const loadTenantRentLedger = async (tenantId) => {
    const response = await apiFetch(`/tenants/${tenantId}/rent-ledger`);
    if (response.ok) {
      setTotalsEditLedger(await response.json());
    } else {
      setTotalsEditLedger(null);
    }
  };

  const startEditTenantRent = (tenant) => {
    setRentEditOnly(true);
    setEditingTenantId(tenant.id);
    loadTenantRentLedger(tenant.id);
    setFormData({
      tenant_name: tenant.tenant_name || "",
      property_name: tenant.property_name || "",
      entity_name: tenant.entity_name || activeEntity,
      advance_paid: tenant.advance_paid || "",
      advance_paid_date: tenant.advance_paid_date || "",
      exempted_amount: tenant.exempted_amount ?? "",
      taxable_amount: tenant.taxable_amount ?? "",
      gst: tenant.gst ?? "",
      total_rent: tenant.total_rent ?? "",
      tds: tenant.tds ?? "",
      monthly_receivable: tenant.monthly_receivable ?? "",
      received: tenant.received ?? "",
      received_fy: tenant.received_fy ?? tenant.received ?? "",
      received_date: tenant.received_date || "",
      dues_month: tenant.dues_month || "",
      remarks: tenant.remarks || ""
    });
    setActiveForm("add-tenant");
  };

  const getMonthReceiptDraft = (tenantId) =>
    monthReceiptDrafts[tenantId] || getDefaultMonthReceiptDraft();

  const updateMonthReceiptDraft = (tenantId, patch) => {
    setMonthReceiptSuccess((prev) => {
      if (!prev[tenantId]) return prev;
      const next = { ...prev };
      delete next[tenantId];
      return next;
    });
    setMonthReceiptDrafts((prev) => ({
      ...prev,
      [tenantId]: { ...(prev[tenantId] || getDefaultMonthReceiptDraft()), ...patch }
    }));
  };

  const handleSaveMonthlyReceipt = async (tenant) => {
    const draft = getMonthReceiptDraft(tenant.id);
    const monthly = getTenantMonthlyReceivable(tenant);
    const response = await apiFetch(`/tenants/${tenant.id}/monthly-receipts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        year_month: draft.year_month,
        received_full: draft.received_full,
        received_amount: draft.received_full ? monthly : amountValueOrZero(draft.received_amount),
        received_date: draft.received_date || null
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to record monthly receipt");
      return;
    }
    setGeneralMessage(`Recorded ${draft.year_month} receipt for ${tenant.tenant_name}`);
    const credited = draft.received_full ? monthly : amountValueOrZero(draft.received_amount);
    setMonthReceiptSuccess((prev) => ({
      ...prev,
      [tenant.id]: `Recorded ${draft.year_month} — ${formatInr(credited)} for ${tenant.tenant_name}`
    }));
    setMonthReceiptDrafts((prev) => {
      const next = { ...prev };
      delete next[tenant.id];
      return next;
    });
    await loadTenants();
  };

  const startEditTenant = (tenant) => {
    setRentEditOnly(false);
    setEditingTenantId(tenant.id);
    loadTenantRentLedger(tenant.id);
    setFormData({
      tenant_name: tenant.tenant_name || "",
      property_name: tenant.property_name || "",
      entity_name: tenant.entity_name || activeEntity,
      advance_paid: tenant.advance_paid || "",
      advance_paid_date: tenant.advance_paid_date || "",
      exempted_amount: tenant.exempted_amount ?? "",
      taxable_amount: tenant.taxable_amount ?? "",
      gst: tenant.gst ?? "",
      total_rent: tenant.total_rent ?? "",
      tds: tenant.tds ?? "",
      monthly_receivable: tenant.monthly_receivable ?? "",
      received: tenant.received ?? "",
      received_fy: tenant.received_fy ?? tenant.received ?? "",
      received_date: tenant.received_date || "",
      dues_month: tenant.dues_month || "",
      remarks: tenant.remarks || ""
    });
    setActiveForm("add-tenant");
  };

  const renderUnpaidMonthSelector = (ledger, selectedLabels, onToggle) => {
    const options =
      ledger?.month_picker_options ||
      (ledger?.months || []).map((item) => item.label) ||
      [];
    if (!options.length) {
      return <p className="rent-mode-hint">Add monthly rent first to see FY months.</p>;
    }
    return (
      <div className="unpaid-month-grid">
        {options.map((label) => {
          const meta = ledger?.months?.find((item) => item.label === label);
          const checked = (selectedLabels || []).includes(label);
          return (
            <label
              key={label}
              className={`unpaid-month-chip ${checked ? "unpaid-month-chip-selected" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(label)}
              />
              <span>{label}</span>
              {meta && (
                <span className={`unpaid-status unpaid-status-${meta.status}`}>{meta.status}</span>
              )}
            </label>
          );
        })}
      </div>
    );
  };

  const renderDuesMonthField = (fieldId = "tenant-dues-month") => (
    <>
      <label className="full-width-label">Rent not received (months)</label>
      {renderUnpaidMonthSelector(
        totalsEditLedger,
        parseDueMonthSelection(formData.dues_month),
        (label) => {
          const current = parseDueMonthSelection(formData.dues_month);
          const next = current.includes(label)
            ? current.filter((item) => item !== label)
            : [...current, label];
          setFormData((prev) => ({
            ...prev,
            dues_month: formatDueMonthSelection(next)
          }));
        }
      )}
      <input
        id={fieldId}
        name="dues_month"
        type="text"
        placeholder="Or type manually: Mar-2026, Apr-2026"
        value={formData.dues_month}
        onChange={handleInputChange}
      />
      <p className="rent-mode-hint">
        Checked months = rent not received. Usually auto-calculated from receipts; override here if needed.
      </p>
    </>
  );

  const renderFinancialsPropertyModal = () => {
    if (!financialsModal) return null;
    const property = groupedPropertyData.find((item) => item.id === financialsModal.propertyId);
    if (!property) return null;
    const propertyTotals = fyTotalsByProperty.find((item) => item.propertyId === property.id);

    return (
      <div className="modal-overlay" role="presentation" onClick={() => setFinancialsModal(null)}>
        <div
          className="card property-modal-card"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal-header">
            <h3>
              {property.property_name} — {financialsModal.mode === "rent" && "Rent structure"}
              {financialsModal.mode === "received" && "Record payments"}
              {financialsModal.mode === "totals" && "FY totals & unpaid months"}
            </h3>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setFinancialsModal(null)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {financialsModal.mode === "rent" && (
            <div className="property-modal-body">
              {property.tenants.length === 0 && <p>No tenants for this property.</p>}
              {property.tenants.map((tenant) => (
                <div className="tenant-calc-card compact" key={`modal-rent-${tenant.id}`}>
                  <div className="tenant-calc-header">
                    <strong>{tenant.tenant_name}</strong>
                    <div className="tenant-calc-actions">
                      <button type="button" className="small-btn" onClick={() => startEditTenantRent(tenant)}>
                        Edit rent
                      </button>
                      <button
                        type="button"
                        className="small-btn danger"
                        onClick={() => handleDeleteTenant(tenant.id, tenant.tenant_name)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="tenant-calc-grid">
                    <div><span className="tenant-calc-label">Exempted</span><span>{formatRentCell(tenant.exempted_amount, inrFormatter)}</span></div>
                    <div><span className="tenant-calc-label">Taxable</span><span>{formatRentCell(tenant.taxable_amount, inrFormatter)}</span></div>
                    <div><span className="tenant-calc-label">GST</span><span>{formatRentCell(tenant.gst, inrFormatter)}</span></div>
                    <div><span className="tenant-calc-label">Total Rent</span><span>{formatRentCell(tenant.total_rent, inrFormatter)}</span></div>
                    <div><span className="tenant-calc-label">TDS</span><span>{formatRentCell(tenant.tds, inrFormatter)}</span></div>
                    <div><span className="tenant-calc-label">Monthly Recv.</span><span className="tenant-calc-recv">{formatInr(getTenantMonthlyReceivable(tenant))}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {financialsModal.mode === "received" && (
            <div className="property-modal-body">
              {property.tenants.map((tenant) => {
                const draft = getMonthReceiptDraft(tenant.id);
                const monthly = getTenantMonthlyReceivable(tenant);
                return (
                  <div className="record-row" key={`modal-rec-${tenant.id}`}>
                    <div className="record-row-main">
                      <span className="record-tenant-name">{tenant.tenant_name}</span>
                      <select
                        className="monthly-month-select"
                        value={draft.year_month}
                        onChange={(event) =>
                          updateMonthReceiptDraft(tenant.id, { year_month: event.target.value })
                        }
                      >
                        {buildFyMonthPickerOptions(resolveTenantFyStartMonth(tenant)).map((month) => (
                          <option key={`${tenant.id}-${month}`} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                      <label className="monthly-received-full">
                        <input
                          type="checkbox"
                          checked={draft.received_full}
                          onChange={(event) =>
                            updateMonthReceiptDraft(tenant.id, { received_full: event.target.checked })
                          }
                        />
                        Full ({formatInr(monthly)})
                      </label>
                      {!draft.received_full && (
                        <input
                          type="text"
                          className="monthly-received-input"
                          placeholder="Amount"
                          value={draft.received_amount}
                          onChange={(event) =>
                            updateMonthReceiptDraft(tenant.id, { received_amount: event.target.value })
                          }
                        />
                      )}
                      <button type="button" className="small-btn" onClick={() => handleSaveMonthlyReceipt(tenant)}>
                        Record
                      </button>
                    </div>
                    {monthReceiptSuccess[tenant.id] && (
                      <span className="record-success">{monthReceiptSuccess[tenant.id]}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {financialsModal.mode === "totals" && propertyTotals && (
            <div className="property-modal-body">
              <table className="rental-table totals-table responsive-data-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Receivable</th>
                    <th>Received</th>
                    <th>Balance</th>
                    <th>Rent not received</th>
                    <th>Remarks</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {propertyTotals.tenants.map((row) => {
                    const tenant = tenants.find((item) => item.id === row.tenantId);
                    const isEditing = totalsEditTenantId === row.tenantId;
                    if (isEditing) {
                      return (
                        <tr key={`modal-edit-${row.tenantId}`} className="totals-edit-row">
                          <td>{row.tenantName}</td>
                          <td>
                            <input
                              name="receivable"
                              className="totals-edit-input"
                              value={totalsEditForm.receivable}
                              onChange={handleTotalsEditInput}
                            />
                          </td>
                          <td>
                            <input
                              name="received_fy"
                              className="totals-edit-input"
                              value={totalsEditForm.received_fy}
                              onChange={handleTotalsEditInput}
                            />
                          </td>
                          <td>{formatInr(amountValueOrZero(totalsEditForm.receivable) - amountValueOrZero(totalsEditForm.received_fy))}</td>
                          <td>
                            {renderUnpaidMonthSelector(
                              totalsEditLedger,
                              totalsEditForm.unpaidMonthLabels,
                              toggleTotalsUnpaidMonth
                            )}
                          </td>
                          <td>
                            <input
                              name="remarks"
                              type="text"
                              className="totals-edit-input totals-edit-remarks"
                              value={totalsEditForm.remarks}
                              onChange={handleTotalsEditInput}
                            />
                          </td>
                          <td className="totals-edit-actions">
                            <button type="button" className="small-btn" onClick={() => saveTotalsEdit(row.tenantId)}>
                              Save
                            </button>
                            <button type="button" className="small-btn secondary" onClick={cancelTotalsEdit}>
                              Cancel
                            </button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={`modal-total-${row.tenantId}`}>
                        <td>{row.tenantName}</td>
                        <td>{formatInr(row.fyReceivable)}</td>
                        <td>{formatInr(row.received)}</td>
                        <td className={getTotalsTenantCurrentClass(row.balance)}>{formatInr(row.balance)}</td>
                        <td className={getDueClass(row.duesMonth)}>
                          {row.unpaidLabels?.length ? (
                            <ul className="unpaid-month-list">
                              {row.unpaidLabels.map((label) => (
                                <li key={`${row.tenantId}-${label}`}>{label}</li>
                              ))}
                            </ul>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="totals-remarks-cell">{row.remarks || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="small-btn"
                            disabled={!tenant}
                            onClick={() => tenant && startTotalsEdit(tenant)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {propertyTotals.tenants.length > 0 && (
                    <tr className="property-total-row">
                      <td><strong>Property total</strong></td>
                      <td><strong>{formatInr(propertyTotals.totals.fyReceivable)}</strong></td>
                      <td><strong>{formatInr(propertyTotals.totals.received)}</strong></td>
                      <td><strong>{formatInr(propertyTotals.totals.balance)}</strong></td>
                      <td colSpan={3} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {financialsModal.mode === "totals" && !propertyTotals && (
            <p>No FY data for this property yet.</p>
          )}
        </div>
      </div>
    );
  };

  const renderSelectEntityPrompt = (sectionLabel) => (
    <div className="card select-entity-prompt">
      <p>
        Select <strong>VTPL</strong>, <strong>RNS</strong>, <strong>HUF</strong>, or <strong>TRS</strong> from
        the sidebar to open {sectionLabel}.
      </p>
    </div>
  );

  const renderRentPreviewFields = () => {
    const isTaxable =
      Boolean(formData.taxable_amount && !formData.exempted_amount) ||
      formRentPreview?.rent_type === "taxable";
    return (
      <>
        <label className="full-width-label" htmlFor="tenant-gst">
          GST (editable before save)
        </label>
        <input
          id="tenant-gst"
          name="gst"
          type="text"
          inputMode="decimal"
          placeholder={isTaxable ? "GST amount" : "NA for exempted"}
          value={formData.gst}
          onChange={handleInputChange}
          disabled={!isTaxable}
        />
        <label className="full-width-label" htmlFor="tenant-total-rent">
          Total Rent (editable before save)
        </label>
        <input
          id="tenant-total-rent"
          name="total_rent"
          type="text"
          inputMode="decimal"
          placeholder="Total rent"
          value={formData.total_rent}
          onChange={handleInputChange}
        />
        <label className="full-width-label" htmlFor="tenant-tds">
          TDS (editable before save)
        </label>
        <input
          id="tenant-tds"
          name="tds"
          type="text"
          inputMode="decimal"
          placeholder={isTaxable ? "TDS amount" : "NA for exempted"}
          value={formData.tds}
          onChange={handleInputChange}
          disabled={!isTaxable}
        />
        <label className="full-width-label" htmlFor="tenant-monthly-receivable">
          Monthly Receivable (editable before save)
        </label>
        <input
          id="tenant-monthly-receivable"
          name="monthly_receivable"
          type="text"
          inputMode="decimal"
          placeholder="Monthly receivable"
          value={formData.monthly_receivable}
          onChange={handleInputChange}
        />
      </>
    );
  };

  const handleDeleteTenant = async (tenantId, tenantName = "this tenant") => {
    if (
      !window.confirm(
        `Delete ${tenantName}? This removes the tenant from Financials, Totals, Tenant Details, and Documents.`
      )
    ) {
      return;
    }
    const response = await apiFetch(`/tenants/${tenantId}`, { method: "DELETE" });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to delete tenant");
      return;
    }
    if (String(editingTenantId) === String(tenantId)) {
      resetTenantForm();
    }
    if (String(totalsEditTenantId) === String(tenantId)) {
      cancelTotalsEdit();
    }
    if (String(tenantDetailForm.tenant_id) === String(tenantId)) {
      setTenantDetailForm({
        tenant_id: "",
        tenant_email: "",
        tenant_contact_no: "",
        tenant_address: "",
        advance_paid: "",
        advance_paid_date: ""
      });
    }
    setMonthReceiptDrafts((prev) => {
      const next = { ...prev };
      delete next[tenantId];
      return next;
    });
    setMonthReceiptSuccess((prev) => {
      const next = { ...prev };
      delete next[tenantId];
      return next;
    });
    if (String(csEditTenantId) === String(tenantId)) {
      cancelCsEdit();
    }
    setGeneralMessage(result.message || `${tenantName} deleted`);
    await loadTenants();
    await loadDocuments();
    await loadCompleteSummary();
  };

  const handleAddProperty = async (event) => {
    event.preventDefault();
    if (!newPropertyName.trim()) {
      setGeneralMessage("Enter property name");
      return;
    }
    const response = await apiFetch("/properties", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity_name: activeEntity, property_name: newPropertyName.trim() })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to add property");
      return;
    }
    setGeneralMessage("Property added");
    setNewPropertyName("");
    setActiveForm("");
    await loadProperties();
    setActiveSection("property-details");
  };

  const handleUploadInput = (event) => {
    const { name, value, files } = event.target;
    if (name === "file") {
      setUploadInfo((prev) => ({ ...prev, file: files && files[0] ? files[0] : null }));
      return;
    }
    setUploadInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadInfo.file || !uploadInfo.property_name || !uploadInfo.document_name.trim()) {
      setGeneralMessage("Select property, document name and file first");
      return;
    }
    const body = new FormData();
    body.append("file", uploadInfo.file);
    body.append("tenant_id", uploadInfo.tenant_id);
    body.append("entity_name", activeEntity);
    body.append("property_name", uploadInfo.property_name);
    body.append("document_name", uploadInfo.document_name.trim());
    const response = await apiFetch("/upload", { method: "POST", body });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Upload failed");
      return;
    }
    setGeneralMessage(`Uploaded: ${result.file_name}`);
    setUploadInfo({ tenant_id: "", property_name: "", document_name: "", file: null });
    setActiveForm("");
    await loadDocuments();
    const fileInput = document.getElementById("doc-file");
    if (fileInput) fileInput.value = "";
  };

  const cancelEditProperty = () => {
    setPropertyDetailPropertyId("");
    setPropertyDetailForm({
      property_name: "",
      physical_address: "",
      google_map_location: "",
      building_type: "",
      sq_ft: "",
      other_comments: ""
    });
  };

  const handleEntityChange = async (entity) => {
    setActiveEntity(entity);
    setSelectedTenantId(null);
    cancelEditProperty();
    setEditingTenantId(null);
    setTotalsEditTenantId(null);
    setActiveForm("");
    setActiveSection("financials");
    setPassSheetResult(null);
    setPassSheetApprovals({});
    setPassSheetDueMonths({});
    setMonthReceiptSuccess({});
    setMonthReceiptDrafts({});
    setFormData((prev) => ({ ...prev, entity_name: entity, property_name: "" }));
    setUploadInfo({ tenant_id: "", property_name: "", document_name: "", file: null });
    setTenantDetailForm({
      tenant_id: "",
      tenant_email: "",
      tenant_contact_no: "",
      tenant_address: "",
      advance_paid: "",
      advance_paid_date: ""
    });
    await loadTenants(entity);
    await loadDocuments(entity);
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setIsLoggedIn(false);
    setUserEmail("");
    setShowResetConfirm(false);
    setResetPassword("");
    setResetError("");
    setActiveEntity("");
    setTenants([]);
    setDocuments([]);
    setActiveSection("financials");
    setActiveForm("");
    setLoginForm({ email: "", password: "" });
    setLoginStep("credentials");
    setTotpCode("");
    setTotpSetup({ qr_data_url: "", manual_secret: "" });
    setLoginError("");
    setGeneralMessage("");
  };

  const openDataResetConfirm = () => {
    setResetPassword("");
    setResetError("");
    setShowResetConfirm(true);
  };

  const cancelDataReset = () => {
    setShowResetConfirm(false);
    setResetPassword("");
    setResetError("");
  };

  useEffect(() => {
    if (!activeForm && !propertyDetailPropertyId && !showResetConfirm) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (showResetConfirm) {
        cancelDataReset();
        return;
      }
      if (propertyDetailPropertyId) {
        cancelEditProperty();
        return;
      }
      closeActiveForm();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeForm, propertyDetailPropertyId, showResetConfirm]);

  const handleDataReset = async (event) => {
    event.preventDefault();
    if (!resetPassword) {
      setResetError("Enter your password to confirm");
      return;
    }

    setResetSubmitting(true);
    setResetError("");
    const response = await apiFetch("/data/reset-fy", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password: resetPassword })
    });
    const result = await response.json();
    setResetSubmitting(false);

    if (!response.ok) {
      setResetError(result.message || "Unable to reset FY data");
      return;
    }

    cancelDataReset();
    cancelTotalsEdit();
    cancelCsEdit();
    resetTenantForm();
    setMonthReceiptDrafts({});
    setMonthReceiptSuccess({});
    await loadTenants();
    await loadCompleteSummary();
    setGeneralMessage(result.message || "FY rent data reset");
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Delete this document?")) return;

    const response = await apiFetch(`/documents/${documentId}`, { method: "DELETE" });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to delete document");
      return;
    }

    setGeneralMessage("Document deleted");
    await loadDocuments();
  };

  const handleDownloadDocument = async (doc) => {
    const response = await apiFetch(`/documents/${doc.id}/download`);
    if (!response.ok) {
      setGeneralMessage("Unable to download document");
      return;
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = doc.file_name || "document";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleTenantDetailSelection = (event) => {
    const tenantId = event.target.value;
    if (!tenantId) {
      setTenantDetailForm({
        tenant_id: "",
        tenant_email: "",
        tenant_contact_no: "",
        tenant_address: "",
        advance_paid: "",
        advance_paid_date: ""
      });
      return;
    }
    const selectedTenant = tenants.find((item) => String(item.id) === String(tenantId));
    if (!selectedTenant) return;
    setTenantDetailForm({
      tenant_id: String(selectedTenant.id),
      tenant_email: selectedTenant.tenant_email || "",
      tenant_contact_no: selectedTenant.tenant_contact_no || "",
      tenant_address: selectedTenant.tenant_address || "",
      advance_paid: selectedTenant.advance_paid ?? "",
      advance_paid_date: selectedTenant.advance_paid_date || ""
    });
  };

  const handleTenantDetailInput = (event) => {
    const { name, value } = event.target;
    setTenantDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveTenantDetails = async (event) => {
    event.preventDefault();
    if (!tenantDetailForm.tenant_id) {
      setGeneralMessage("Select a tenant first");
      return;
    }
    const selectedTenant = tenants.find(
      (item) => String(item.id) === String(tenantDetailForm.tenant_id)
    );
    if (!selectedTenant) {
      setGeneralMessage("Tenant not found");
      return;
    }
    const response = await apiFetch(`/tenants/${selectedTenant.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_name: selectedTenant.tenant_name,
        property_name: selectedTenant.property_name,
        entity_name: selectedTenant.entity_name,
        exempted_amount: selectedTenant.exempted_amount,
        taxable_amount: selectedTenant.taxable_amount,
        received: selectedTenant.received,
        received_fy: selectedTenant.received_fy,
        received_date: selectedTenant.received_date,
        dues_month: selectedTenant.dues_month,
        remarks: selectedTenant.remarks,
        advance_paid: amountValueOrZero(tenantDetailForm.advance_paid),
        advance_paid_date: tenantDetailForm.advance_paid_date || null,
        tenant_email: tenantDetailForm.tenant_email,
        tenant_contact_no: tenantDetailForm.tenant_contact_no,
        tenant_address: tenantDetailForm.tenant_address
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save tenant details");
      return;
    }
    setGeneralMessage("Tenant details updated");
    setActiveForm("");
    setTenantDetailForm({
      tenant_id: "",
      tenant_email: "",
      tenant_contact_no: "",
      tenant_address: "",
      advance_paid: "",
      advance_paid_date: ""
    });
    await loadTenants();
  };

  const formatInr = (value) => inrFormatter.format(Number(value || 0));

  const cycleCompleteSummaryHighlight = async (tenantId, yearMonth, currentHighlight) => {
    const next = nextMonthHighlight(currentHighlight);
    const response = await apiFetch("/complete-summary/month-highlight", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_id: tenantId,
        year_month: yearMonth,
        highlight: next
      })
    });
    if (!response.ok) {
      const result = await response.json();
      setGeneralMessage(result.message || "Unable to update highlight");
      return;
    }
    await loadCompleteSummary();
  };

  const buildCsEditDraft = (row) => {
    const months = {};
    const monthKeys = completeSummary.months.length ? completeSummary.months : fyMonthColumns.months;
    monthKeys.forEach((monthKey) => {
      const monthData = row.months?.[monthKey] || { transactions: [] };
      months[monthKey] = monthData.transactions.map((txn) => ({
        id: txn.id,
        transaction_date: txn.transaction_date || "",
        transaction_ref: txn.transaction_ref || "",
        amount: txn.amount === 0 || txn.amount ? String(txn.amount) : ""
      }));
    });
    return {
      months,
      deletedTxnIds: []
    };
  };

  const startCsEdit = (row) => {
    setCsEditTenantId(row.tenant_id);
    setCsEditDraft(buildCsEditDraft(row));
  };

  const cancelCsEdit = () => {
    setCsEditTenantId(null);
    setCsEditDraft(null);
  };

  const updateCsDraftTxn = (monthKey, index, field, value) => {
    setCsEditDraft((prev) => {
      if (!prev) return prev;
      const txns = [...(prev.months[monthKey] || [])];
      txns[index] = { ...txns[index], [field]: value };
      return { ...prev, months: { ...prev.months, [monthKey]: txns } };
    });
  };

  const addCsDraftTxn = (monthKey) => {
    setCsEditDraft((prev) => {
      if (!prev) return prev;
      const txns = [...(prev.months[monthKey] || []), {
        id: null,
        transaction_date: "",
        transaction_ref: "",
        amount: ""
      }];
      return { ...prev, months: { ...prev.months, [monthKey]: txns } };
    });
  };

  const removeCsDraftTxn = (monthKey, index) => {
    setCsEditDraft((prev) => {
      if (!prev) return prev;
      const txns = [...(prev.months[monthKey] || [])];
      const removed = txns.splice(index, 1)[0];
      const deletedTxnIds = removed?.id
        ? [...prev.deletedTxnIds, removed.id]
        : prev.deletedTxnIds;
      return {
        ...prev,
        months: { ...prev.months, [monthKey]: txns },
        deletedTxnIds
      };
    });
  };

  const csDraftMonthTotal = (monthKey) => {
    if (!csEditDraft) return 0;
    return (csEditDraft.months[monthKey] || []).reduce(
      (sum, txn) => sum + amountValueOrZero(txn.amount),
      0
    );
  };

  const saveCsEdit = async (tenantId) => {
    if (!csEditDraft) return;

    for (const txnId of csEditDraft.deletedTxnIds) {
      const delResponse = await apiFetch(`/complete-summary/transactions/${txnId}`, {
        method: "DELETE"
      });
      if (!delResponse.ok) {
        const result = await delResponse.json();
        setGeneralMessage(result.message || "Unable to delete transaction");
        return;
      }
    }

    for (const [monthKey, txns] of Object.entries(csEditDraft.months)) {
      for (const txn of txns) {
        const payload = {
          transaction_date: txn.transaction_date,
          transaction_ref: txn.transaction_ref,
          amount: amountValueOrZero(txn.amount)
        };
        if (txn.id) {
          const response = await apiFetch(`/complete-summary/transactions/${txn.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
          });
          if (!response.ok) {
            const result = await response.json();
            setGeneralMessage(result.message || "Unable to update transaction");
            return;
          }
        } else {
          const response = await apiFetch("/complete-summary/transactions", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tenant_id: tenantId,
              year_month: monthKey,
              ...payload
            })
          });
          if (!response.ok) {
            const result = await response.json();
            setGeneralMessage(result.message || "Unable to add transaction");
            return;
          }
        }
      }
    }

    setGeneralMessage("Row saved");
    cancelCsEdit();
    await loadCompleteSummary();
  };

  const formatCsCellAmount = (value) => {
    const parsed = amountValueOrZero(value);
    if (!parsed && (value === "" || value === null || value === undefined)) return "—";
    return formatInr(parsed);
  };

  const formatCsCellText = (value) => (value ? String(value) : "—");

  const handleCompleteSummaryExport = async () => {
    setGeneralMessage("");
    const response = await apiFetch("/complete-summary/export");
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      setGeneralMessage(result.message || "Unable to export Complete Summary");
      return;
    }

    const disposition = response.headers.get("Content-Disposition") || "";
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = utf8Match
      ? decodeURIComponent(utf8Match[1])
      : plainMatch?.[1] || "Complete_Summary.xlsx";

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
    setGeneralMessage(`Exported ${filename}`);
  };

  const startEditProperty = (property) => {
    setPropertyDetailPropertyId(String(property.id));
    setPropertyDetailForm({
      property_name: property.property_name || "",
      physical_address: property.physical_address || "",
      google_map_location: property.google_map_location || "",
      building_type: property.building_type || "",
      sq_ft: property.sq_ft || "",
      other_comments: property.other_comments || ""
    });
  };

  const handlePropertyDetailChange = (event) => {
    const { name, value } = event.target;
    setPropertyDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const savePropertyDetails = async () => {
    if (!propertyDetailPropertyId) {
      setGeneralMessage("Select a property first");
      return;
    }
    const response = await apiFetch(`/properties/${propertyDetailPropertyId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(propertyDetailForm)
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save property details");
      return;
    }
    setGeneralMessage("Property details updated");
    setPropertyDetailPropertyId("");
    await loadProperties();
    await loadTenants();
    await loadDocuments();
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("Delete this property? This will also delete its tenants and documents.")) return;
    const response = await apiFetch(`/properties/${propertyId}`, { method: "DELETE" });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to delete property");
      return;
    }
    setGeneralMessage("Property deleted");
    if (String(propertyDetailPropertyId) === String(propertyId)) cancelEditProperty();
    await loadProperties();
    await loadTenants();
    await loadDocuments();
  };

  if (!isLoggedIn) {
    return (
      <div className="login-wrapper">
        {loginStep === "credentials" && (
          <form className="card login-card" onSubmit={handlePasswordLogin}>
            <h2>Rent Tracker Login</h2>
            <label>Email</label>
            <input
              type="email"
              value={loginForm.email}
              onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
              required
            />
            <label>Password</label>
            <input
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
              required
            />
            {loginError && <p className="error">{loginError}</p>}
            <button type="submit">Continue</button>
          </form>
        )}

        {loginStep === "setup" && (
          <form className="card login-card login-card-wide" onSubmit={handleVerifyTotp}>
            <h2>Set up Google Authenticator</h2>
            <p className="info">
              Scan this QR code in Google Authenticator (or Authy), then enter the 6-digit code to
              finish setup.
            </p>
            {totpSetup.qr_data_url && (
              <img className="totp-qr" src={totpSetup.qr_data_url} alt="Authenticator QR code" />
            )}
            {totpSetup.manual_secret && (
              <p className="totp-secret">
                Manual key: <code>{totpSetup.manual_secret}</code>
              </p>
            )}
            <label>6-digit code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
            {loginError && <p className="error">{loginError}</p>}
            <button type="submit">Confirm and login</button>
            <button type="button" className="secondary" onClick={handleBackToCredentials}>
              Back
            </button>
          </form>
        )}

        {loginStep === "totp" && (
          <form className="card login-card" onSubmit={handleVerifyTotp}>
            <h2>Authenticator code</h2>
            <p className="info">Open Google Authenticator and enter the 6-digit code for {loginForm.email}</p>
            <label>6-digit code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
            {loginError && <p className="error">{loginError}</p>}
            <button type="submit">Verify and login</button>
            <button type="button" className="secondary" onClick={handleBackToCredentials}>
              Back
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className={`layout ${isSidebarCollapsed ? "sidebar-collapsed" : ""}${activeEntity ? ` entity-${activeEntity}` : ""}`}>
      {!isSidebarCollapsed && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}
      {isSidebarCollapsed && (
        <button
          className="sidebar-reopen-btn"
          type="button"
          onClick={() => setIsSidebarCollapsed(false)}
        >
          Menu
        </button>
      )}
      <aside className="sidebar">
        <button
          className="sidebar-toggle-btn"
          type="button"
          onClick={() => setIsSidebarCollapsed((prev) => !prev)}
        >
          {isSidebarCollapsed ? "Show Menu" : "Hide Menu"}
        </button>
        <h2>Rent Tracker</h2>
        <button
          className={`sidebar-main-btn ${activeSection === "complete-summary" ? "active" : ""}`}
          onClick={() => {
            setActiveSection("complete-summary");
            setActiveForm("");
            cancelEditProperty();
            cancelCsEdit();
          }}
        >
          Complete Summary
        </button>
        <div className="sidebar-divider" />
        {ENTITY_OPTIONS.map((entity) => (
          <button
            key={entity}
            type="button"
            className={`sidebar-entity-btn ${activeEntity === entity ? "active" : ""}`}
            onClick={() => handleEntityChange(entity)}
          >
            {entity}
          </button>
        ))}
        <div className="sidebar-divider" />
        <button
          className={activeSection === "financials" ? "active" : ""}
          onClick={() => {
            setActiveSection("financials");
            setActiveForm("");
            cancelEditProperty();
          }}
        >
          Financials
        </button>
        <button
          className={activeSection === "property-details" ? "active" : ""}
          onClick={() => {
            setActiveSection("property-details");
            setActiveForm("");
          }}
        >
          Property Details
        </button>
        <button
          className={activeSection === "documents" ? "active" : ""}
          onClick={() => {
            setActiveSection("documents");
            setActiveForm("");
            cancelEditProperty();
          }}
        >
          View Documents
        </button>
        <button
          className={activeSection === "tenant-details" ? "active" : ""}
          onClick={() => {
            setActiveSection("tenant-details");
            setActiveForm("");
            cancelEditProperty();
          }}
        >
          Tenant Details
        </button>
        <button
          className={activeSection === "pass-sheet" ? "active" : ""}
          onClick={() => {
            setActiveSection("pass-sheet");
            setActiveForm("");
            cancelEditProperty();
          }}
        >
          Pass Sheet Scanner
        </button>
        <div className="sidebar-footer">
          <button type="button" className="data-reset-btn" onClick={openDataResetConfirm}>
            Data Reset
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      <main className="content">
        {showResetConfirm && (
          <div className="modal-overlay" role="presentation" onClick={cancelDataReset}>
            <form
              className="card reset-confirm-card"
              onSubmit={handleDataReset}
              onClick={(event) => event.stopPropagation()}
            >
              <h3>Confirm FY data reset</h3>
              <p className="reset-confirm-warning">
                This clears all received amounts, monthly receipts, Complete Summary entries, dues,
                and remarks. Tenants, properties, rent amounts, advance paid, and documents are
                kept. This cannot be undone.
              </p>
              <label htmlFor="reset-email">Account</label>
              <input id="reset-email" type="email" value={userEmail} readOnly />
              <label htmlFor="reset-password">Password</label>
              <input
                id="reset-password"
                type="password"
                autoComplete="current-password"
                value={resetPassword}
                onChange={(event) => setResetPassword(event.target.value)}
                placeholder="Re-enter your password"
                required
              />
              {resetError && <p className="error">{resetError}</p>}
              <div className="reset-confirm-actions">
                <button type="button" className="btn-secondary" onClick={cancelDataReset} disabled={resetSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-danger" disabled={resetSubmitting}>
                  {resetSubmitting ? "Resetting…" : "Reset FY data"}
                </button>
              </div>
            </form>
          </div>
        )}
        {generalMessage && <p className="info">{generalMessage}</p>}
        <h1>
          {activeSection === "complete-summary" ? "Complete Summary" : activeEntity || "Select an entity"}
        </h1>
        {activeSection === "financials" && (
          !activeEntity ? (
            renderSelectEntityPrompt("Financials")
          ) : (
          <>
        <div className="card action-row">
          <button type="button" className="btn-primary" onClick={openAddTenantForm}>
            Add Tenant
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setActiveForm("upload-docs")}
          >
            Upload Documents
          </button>
        </div>

        {activeForm === "add-tenant" && (
          <div className="modal-overlay" role="presentation" onClick={closeActiveForm}>
          <form
            className="card form-grid modal-card"
            onSubmit={handleTenantSubmit}
            noValidate
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
            <h3>
              {editingTenantId
                ? rentEditOnly
                  ? "Edit monthly rent"
                  : "Edit Tenant"
                : "Add Tenant"}
            </h3>
            <button type="button" className="modal-close-btn" onClick={closeActiveForm} aria-label="Close">
              ×
            </button>
            </div>
            {editingTenantId ? (
              <>
                <p className="edit-tenant-summary">
                  <strong>{formData.tenant_name}</strong> — {formData.property_name}
                </p>
                <p className="rent-mode-hint">
                  Enter either Exempted or Taxable — values auto-fill below. You can edit GST, Total Rent, TDS, and Monthly Receivable before saving.
                </p>
                <input
                  name="exempted_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Exempted (e.g. 50000 or 50,000)"
                  value={formData.exempted_amount}
                  onChange={handleInputChange}
                />
                <input
                  name="taxable_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Taxable (e.g. 1794200 or 17,94,200)"
                  value={formData.taxable_amount}
                  onChange={handleInputChange}
                />
                {renderRentPreviewFields()}
                {!rentEditOnly && (
                  <>
                <div className="computed-field">
                  Cumulative Receivable: {formatRentCell(editingTenant?.receivable, inrFormatter)}
                </div>
                <input
                  name="received"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received (Monthly calculation)"
                  value={formData.received}
                  onChange={handleInputChange}
                />
                <div className="computed-field">
                  Monthly Balance: {inrFormatter.format(editFormBalance)}
                </div>
                <input
                  name="received_fy"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received (Totals / FY)"
                  value={formData.received_fy}
                  onChange={handleInputChange}
                />
                <div className="computed-field">FY Balance: {inrFormatter.format(editFormFyBalance)}</div>
                <input
                  name="received_date"
                  type="text"
                  placeholder="Payment date (MM/DD/YYYY)"
                  value={formData.received_date}
                  onChange={handleInputChange}
                />
                <input
                  name="remarks"
                  placeholder="Remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
                  </>
                )}
                {renderDuesMonthField("edit-tenant-dues-month")}
              </>
            ) : (
              <>
                <input
                  name="tenant_name"
                  placeholder="Tenant Name"
                  value={formData.tenant_name}
                  onChange={handleInputChange}
                  required
                />
                <select name="property_name" value={formData.property_name} onChange={handleInputChange} required>
                  <option value="">Select Property</option>
                  {entityProperties.map((item) => (
                    <option key={item.id} value={item.property_name}>
                      {item.property_name}
                    </option>
                  ))}
                </select>
                <input name="entity_name" value={activeEntity} disabled />
                <p className="rent-mode-hint full-width-label">
                  Monthly rent — enter either Exempted or Taxable (not both). GST, Total Rent, TDS, and Monthly Receivable can be adjusted before save.
                </p>
                <input
                  name="exempted_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Exempted (e.g. 50000 or 50,000)"
                  value={formData.exempted_amount}
                  onChange={handleInputChange}
                />
                <input
                  name="taxable_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Taxable (e.g. 1794200 or 17,94,200)"
                  value={formData.taxable_amount}
                  onChange={handleInputChange}
                />
                {renderRentPreviewFields()}
                <input
                  name="received"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received — Monthly (optional)"
                  value={formData.received}
                  onChange={handleInputChange}
                />
                <input
                  name="received_fy"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received — Totals / FY (optional)"
                  value={formData.received_fy}
                  onChange={handleInputChange}
                />
                <input
                  name="received_date"
                  type="text"
                  placeholder="Payment date (MM/DD/YYYY)"
                  value={formData.received_date}
                  onChange={handleInputChange}
                />
                {editingTenantId ? (
                  renderDuesMonthField()
                ) : (
                  <input
                    name="dues_month"
                    placeholder="Due month (e.g., Mar-2026)"
                    value={formData.dues_month}
                    onChange={handleInputChange}
                  />
                )}
                <input
                  name="remarks"
                  placeholder="Remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
              </>
            )}
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingTenantId ? "Update" : "Add"}
              </button>
              <button type="button" className="btn-secondary" onClick={closeActiveForm}>
                Cancel
              </button>
            </div>
          </form>
          </div>
        )}

        {activeForm === "upload-docs" && (
          <div className="modal-overlay" role="presentation" onClick={closeActiveForm}>
          <form
            className="card form-grid modal-card"
            onSubmit={handleUpload}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Upload Documents</h3>
              <button type="button" className="modal-close-btn" onClick={closeActiveForm} aria-label="Close">
                ×
              </button>
            </div>
            <select
              name="property_name"
              value={uploadInfo.property_name}
              onChange={handleUploadInput}
              required
            >
              <option value="">Select Property</option>
              {entityProperties.map((item) => (
                <option key={`upload-${item.id}`} value={item.property_name}>
                  {item.property_name}
                </option>
              ))}
            </select>
            <select name="tenant_id" value={uploadInfo.tenant_id} onChange={handleUploadInput}>
              <option value="">Select tenant (optional)</option>
              {tenants
                .filter((item) =>
                  uploadInfo.property_name ? item.property_name === uploadInfo.property_name : true
                )
                .map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.tenant_name}
                  </option>
                ))}
            </select>
            <input
              name="document_name"
              placeholder="Document Name"
              value={uploadInfo.document_name}
              onChange={handleUploadInput}
              required
            />
            <input
              id="doc-file"
              name="file"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleUploadInput}
              required
            />
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                Upload
              </button>
              <button type="button" className="btn-secondary" onClick={closeActiveForm}>
                Cancel
              </button>
            </div>
          </form>
          </div>
        )}

        <div className="financials-summary">
          <p className="financials-summary-label">
            Totals (each tenant uses its property FY cycle)
          </p>
          <div className="cards">
            <div className="card stat-card">
              <p>Total Receivable (FY)</p>
              <h3>{formatInr(entityTotals.totalReceivable)}</h3>
            </div>
            <div className="card stat-card">
              <p>Total Received</p>
              <h3>{formatInr(entityTotals.totalReceived)}</h3>
            </div>
            <div className="card stat-card">
              <p>Total Balance</p>
              <h3>{formatInr(entityTotals.totalBalance)}</h3>
            </div>
          </div>
        </div>

        <p className="financials-hub-intro">
          Choose a property, then open one action at a time — rent structure, record payments, or FY totals.
        </p>
        <div className="property-hub-grid">
          {groupedPropertyData.length === 0 && (
            <p className="card property-hub-empty">No properties yet. Add one under Property Details.</p>
          )}
          {groupedPropertyData.map((property) => {
            const propertyTotals = fyTotalsByProperty.find((item) => item.propertyId === property.id);
            const unpaidCount = propertyTotals?.tenants?.reduce(
              (sum, row) => sum + (row.unpaidLabels?.length || 0),
              0
            );
            return (
              <div className="card property-hub-card" key={`hub-${property.id}`}>
                <h3>{property.property_name}</h3>
                <p className="property-hub-meta">
                  {property.tenants.length} tenant{property.tenants.length === 1 ? "" : "s"}
                  {unpaidCount > 0 && (
                    <span className="property-hub-alert"> · {unpaidCount} unpaid month(s)</span>
                  )}
                </p>
                <div className="property-hub-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setFinancialsModal({ propertyId: property.id, mode: "rent" })}
                  >
                    Rent structure
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setFinancialsModal({ propertyId: property.id, mode: "received" })}
                  >
                    Record payments
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => setFinancialsModal({ propertyId: property.id, mode: "totals" })}
                  >
                    FY totals &amp; dues
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {renderFinancialsPropertyModal()}

        </>
          )
        )}

        {activeSection === "documents" && (
          !activeEntity ? (
            renderSelectEntityPrompt("Documents")
          ) : (
          <div className="card">
            <h3>Documents by Property</h3>
            {Object.keys(documentsByProperty).length === 0 && <p>No documents uploaded yet.</p>}
            {Object.entries(documentsByProperty).map(([propertyName, propertyDocs]) => (
              <div key={propertyName} className="doc-property-block">
                <h4>{propertyName}</h4>
                <div className="table-card">
                  <table className="rental-table responsive-data-table">
                    <thead>
                      <tr>
                        <th>Document Name</th>
                        <th>File Name</th>
                        <th>Uploaded At</th>
                        <th>Download</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propertyDocs.map((doc) => (
                        <tr key={doc.id}>
                          <td data-label="Document Name">{doc.document_name || "-"}</td>
                          <td data-label="File Name">{doc.file_name}</td>
                          <td data-label="Uploaded At">{formatDisplayDate(doc.uploaded_at)}</td>
                          <td data-label="Download" className="cell-action">
                            <button className="small-btn" onClick={() => handleDownloadDocument(doc)}>
                              Download
                            </button>
                          </td>
                          <td data-label="Delete" className="cell-action">
                            <button className="small-btn danger" onClick={() => handleDeleteDocument(doc.id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
          )
        )}

        {activeSection === "property-details" && (
          !activeEntity ? (
            renderSelectEntityPrompt("Property Details")
          ) : (
          <div className="card property-details-section">
            <div className="section-header-row">
              <h3>Property Details — {activeEntity}</h3>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setActiveForm("add-property")}
              >
                Add Property
              </button>
            </div>

            {activeForm === "add-property" && (
              <div className="modal-overlay" role="presentation" onClick={closeActiveForm}>
                <form
                  className="card form-grid modal-card modal-card-sm"
                  onSubmit={handleAddProperty}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="modal-header">
                    <h3>Add Property</h3>
                    <button type="button" className="modal-close-btn" onClick={closeActiveForm} aria-label="Close">
                      ×
                    </button>
                  </div>
                  <input
                    placeholder={`New property name for ${activeEntity}`}
                    value={newPropertyName}
                    onChange={(e) => setNewPropertyName(e.target.value)}
                    required
                    autoFocus
                  />
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      Add Property
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeActiveForm}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {propertyDetailPropertyId && (
              <div className="modal-overlay" role="presentation" onClick={cancelEditProperty}>
                <form
                  className="card form-grid modal-card"
                  onSubmit={(event) => {
                    event.preventDefault();
                    savePropertyDetails();
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="modal-header">
                    <h3>Edit Property Details</h3>
                    <button type="button" className="modal-close-btn" onClick={cancelEditProperty} aria-label="Close">
                      ×
                    </button>
                  </div>
                  <input
                    name="property_name"
                    placeholder="Property Name"
                    value={propertyDetailForm.property_name}
                    onChange={handlePropertyDetailChange}
                    required
                  />
                  <input
                    name="physical_address"
                    placeholder="Physical Address"
                    value={propertyDetailForm.physical_address}
                    onChange={handlePropertyDetailChange}
                  />
                  <input
                    name="google_map_location"
                    type="url"
                    placeholder="Google Maps link (https://maps.google.com/...)"
                    value={propertyDetailForm.google_map_location}
                    onChange={handlePropertyDetailChange}
                  />
                  <input
                    name="building_type"
                    placeholder="Building Type"
                    value={propertyDetailForm.building_type}
                    onChange={handlePropertyDetailChange}
                  />
                  <input
                    name="sq_ft"
                    placeholder="Sq Ft (if any)"
                    value={propertyDetailForm.sq_ft}
                    onChange={handlePropertyDetailChange}
                  />
                  <input
                    name="other_comments"
                    placeholder="Other Comments"
                    value={propertyDetailForm.other_comments}
                    onChange={handlePropertyDetailChange}
                  />
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      Save
                    </button>
                    <button type="button" className="btn-secondary" onClick={cancelEditProperty}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {entityProperties.length === 0 && (
              <p>No properties added yet. Use Add Property to create one.</p>
            )}

            <div className="property-details-list">
              {entityProperties.map((property) => (
                  <div className="property-detail-card" key={`property-detail-${property.id}`}>
                    <div className="property-detail-card-header">
                      <h4>{property.property_name}</h4>
                      <div className="property-detail-actions">
                        <button
                          type="button"
                          className="small-btn btn-secondary"
                          onClick={() => startEditProperty(property)}
                        >
                          Edit Details
                        </button>
                        <button
                          type="button"
                          className="small-btn btn-danger"
                          onClick={() => handleDeleteProperty(property.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <dl className="property-detail-fields">
                      <div>
                        <dt>Physical Address</dt>
                        <dd>{property.physical_address || "-"}</dd>
                      </div>
                      <div>
                        <dt>Google Maps</dt>
                        <dd>
                          <GoogleMapLink value={property.google_map_location} />
                        </dd>
                      </div>
                      <div>
                        <dt>Building Type</dt>
                        <dd>{property.building_type || "-"}</dd>
                      </div>
                      <div>
                        <dt>Sq Ft</dt>
                        <dd>{property.sq_ft || "-"}</dd>
                      </div>
                      <div>
                        <dt>Other Comments</dt>
                        <dd>{property.other_comments || "-"}</dd>
                      </div>
                    </dl>
                  </div>
              ))}
            </div>
          </div>
          )
        )}

        {activeSection === "pass-sheet" && (
          !activeEntity ? (
            renderSelectEntityPrompt("Pass Sheet Scanner")
          ) : (
          <div className="card pass-sheet-section">
            <h3>Pass Sheet Scanner — {activeEntity}</h3>
            <p className="pass-sheet-help">
              Upload your bank pass sheet Excel file. Matching uses narration rules per entity.
              On approval, both Monthly calculation and Totals received are updated. Deposits
              within ₹10–99 of monthly receivable are rounded to the receivable amount; larger
              differences are reflected in balance.
            </p>

            <form className="form-grid pass-sheet-settings" onSubmit={saveOpenAiKey}>
              <label className="full-width-label">
                OpenAI API Key {openAiConfigured ? "(configured)" : "(optional — improves matching)"}
              </label>
              <input
                type="password"
                placeholder="sk-..."
                value={openAiKeyInput}
                onChange={(e) => setOpenAiKeyInput(e.target.value)}
                autoComplete="off"
              />
              <button type="submit" className="secondary">
                Save API Key
              </button>
            </form>

            <form className="pass-sheet-upload" onSubmit={handlePassSheetScan}>
              <input
                id="pass-sheet-file"
                type="file"
                accept=".xls,.xlsx"
                onChange={handlePassSheetFile}
              />
              <button type="submit" disabled={passSheetScanning || !passSheetFile}>
                {passSheetScanning ? "Scanning…" : "Scan Pass Sheet"}
              </button>
            </form>

            {passSheetResult && (
              <div className="pass-sheet-results">
                <p>
                  <strong>{passSheetResult.rent_payments_found}</strong> of{" "}
                  <strong>{passSheetResult.expected_rent_count}</strong> expected rent payment(s) found.
                </p>

                {passSheetResult.show_missing_rent_alert && passSheetResult.missing_tenants?.length > 0 && (
                  <div className="pass-sheet-alert">
                    <h4>Rent not received — {passSheetResult.missing_tenants.length} property(ies)</h4>
                    <ul>
                      {passSheetResult.missing_tenants.map((item) => (
                        <li key={item.rule_label}>
                          <strong>{item.property_name || item.rule_label}</strong>
                          {item.tenant_name && item.tenant_name !== item.rule_label
                            ? ` — ${item.tenant_name}`
                            : ""}
                        </li>
                      ))}
                    </ul>
                    <p className="pass-sheet-alert-note">
                      These will be marked with due month one month behind and remark &quot;rent not
                      received&quot; when you approve.
                    </p>
                  </div>
                )}

                {passSheetResult.matches?.length > 0 && (
                  <>
                    <h4>Rent received — review and approve</h4>
                    <div className="table-card pass-sheet-table-wrap">
                      <table className="pass-sheet-approve-table responsive-data-table">
                        <thead>
                          <tr>
                            <th>Approve</th>
                            <th>Tenant</th>
                            <th>Date</th>
                            <th>Scanned</th>
                            <th>Credited</th>
                            <th>Due Month(s)</th>
                          </tr>
                        </thead>
                        <tbody>
                        {passSheetResult.matches.map((row) => (
                            <tr
                            key={row.match_id}
                              className={!row.tenant_found ? "tenant-missing" : ""}
                            >
                              <td data-label="Approve">
                                <input
                                  type="checkbox"
                                  checked={Boolean(passSheetApprovals[row.match_id])}
                                  disabled={!row.tenant_id}
                                  onChange={() => togglePassSheetApproval(row.match_id)}
                                />
                              </td>
                              <td data-label="Tenant">
                                {row.tenant_name}
                                {!row.tenant_found && (
                                  <span className="warning-tag"> Not in DB</span>
                                )}
                                {row.rounded_to_receivable && (
                                  <span className="rounding-tag"> Rounded</span>
                                )}
                              </td>
                              <td data-label="Date" className="pass-sheet-date-cell">{row.date || "-"}</td>
                              <td data-label="Scanned">{formatInr(row.deposit_amount)}</td>
                              <td data-label="Credited">{formatInr(row.credited_amount ?? row.deposit_amount)}</td>
                              <td data-label="Due Month(s)">
                              <div className="pass-sheet-month-checkboxes">
                                {getDueMonthPickerOptions(row.proposed_dues_month).map((month) => {
                                  const selectedMonths =
                                    passSheetDueMonths[row.match_id] ??
                                    parseDueMonthSelection(row.proposed_dues_month);
                                  return (
                                    <label key={`${row.match_id}-${month}`} className="pass-sheet-month-option">
                                      <input
                                        type="checkbox"
                                        checked={selectedMonths.includes(month)}
                                        disabled={!row.tenant_id}
                                        onChange={(event) =>
                                          togglePassSheetDueMonth(row.match_id, month, event.target.checked)
                                        }
                                      />
                                      <span>{month}</span>
                                    </label>
                                  );
                                })}
                              </div>
                              <div className="pass-sheet-month-summary">
                                {formatDueMonthSelection(
                                  passSheetDueMonths[row.match_id] ??
                                    parseDueMonthSelection(row.proposed_dues_month)
                                ) || "Select month(s)"}
                              </div>
                            </td>
                          </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                    <button type="button" className="apply-pass-sheet-btn" onClick={handlePassSheetApply}>
                      Approve &amp; Apply to Financials
                    </button>
                  </>
                )}

              </div>
            )}

            <div className="pass-sheet-rules-panel card">
              <div className="section-header-row">
                <div>
                  <h4>Excel matching keywords — {activeEntity}</h4>
                  <p className="pass-sheet-rules-help">
                    Narration keywords and tenant name patterns used when scanning pass sheets. Changes apply to the next scan.
                  </p>
                </div>
                <div className="pass-sheet-rules-toolbar">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() =>
                      openPassSheetRuleEditor(
                        { label: "", property_name: "", narration_keywords: [], tenant_name_patterns: [] },
                        null
                      )
                    }
                  >
                    Add rule
                  </button>
                  <button type="button" className="small-btn secondary" onClick={resetPassSheetRulesToDefault}>
                    Reset defaults
                  </button>
                </div>
              </div>

              {passSheetRulesLoading && <p>Loading rules…</p>}
              {!passSheetRulesLoading && passSheetRules.length === 0 && (
                <p>No rules yet. Add a rule or reset to defaults.</p>
              )}

              {!passSheetRulesLoading && passSheetRules.length > 0 && (
                <div className="table-card pass-sheet-rules-table-wrap">
                  <table className="rental-table responsive-data-table">
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th>Property</th>
                        <th>Narration keywords</th>
                        <th>Tenant patterns</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {passSheetRules.map((rule, index) => (
                        <tr key={`ps-rule-${index}`}>
                          <td data-label="Label">{rule.label || "—"}</td>
                          <td data-label="Property">{rule.property_name || "—"}</td>
                          <td data-label="Keywords">{(rule.narration_keywords || []).join(", ") || "—"}</td>
                          <td data-label="Patterns">{(rule.tenant_name_patterns || []).join(", ") || "—"}</td>
                          <td data-label="" className="cell-action">
                            <button type="button" className="small-btn" onClick={() => openPassSheetRuleEditor(rule, index)}>
                              Edit
                            </button>
                            <button type="button" className="small-btn danger" onClick={() => deletePassSheetRule(index)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {passSheetRuleDraft && (
              <div className="modal-overlay" role="presentation" onClick={() => setPassSheetRuleDraft(null)}>
                <form
                  className="card form-grid modal-card pass-sheet-rule-modal"
                  onSubmit={(event) => {
                    event.preventDefault();
                    savePassSheetRuleDraft();
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="modal-header">
                    <h3>{passSheetRuleDraft.index == null ? "Add matching rule" : "Edit matching rule"}</h3>
                    <button
                      type="button"
                      className="modal-close-btn"
                      onClick={() => setPassSheetRuleDraft(null)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <input
                    placeholder="Label (e.g. SJS Enterprises)"
                    value={passSheetRuleDraft.label}
                    onChange={(event) =>
                      setPassSheetRuleDraft((prev) => ({ ...prev, label: event.target.value }))
                    }
                    required
                  />
                  <input
                    placeholder="Property name (must match Property Details)"
                    value={passSheetRuleDraft.property_name}
                    onChange={(event) =>
                      setPassSheetRuleDraft((prev) => ({ ...prev, property_name: event.target.value }))
                    }
                  />
                  <label className="full-width-label">Narration keywords (comma-separated)</label>
                  <textarea
                    rows={3}
                    placeholder="SJS ENTERPRISES, SJS ENT"
                    value={passSheetRuleDraft.narration_keywords}
                    onChange={(event) =>
                      setPassSheetRuleDraft((prev) => ({ ...prev, narration_keywords: event.target.value }))
                    }
                  />
                  <label className="full-width-label">Tenant name patterns (comma-separated, optional)</label>
                  <textarea
                    rows={2}
                    placeholder="sjs, sjs enterprises"
                    value={passSheetRuleDraft.tenant_name_patterns}
                    onChange={(event) =>
                      setPassSheetRuleDraft((prev) => ({ ...prev, tenant_name_patterns: event.target.value }))
                    }
                  />
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      Save rule
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setPassSheetRuleDraft(null)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
          )
        )}

        {activeSection === "tenant-details" && (
          !activeEntity ? (
            renderSelectEntityPrompt("Tenant Details")
          ) : (
          <div className="card">
            <div className="section-header-row">
              <div>
                <h3>Tenant Details by Property</h3>
                <p className="tenant-details-hint">
                  Record contact info and security deposit here after adding a tenant in Financials.
                </p>
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setActiveForm("tenant-details")}
              >
                Update Tenant Details
              </button>
            </div>

            {activeForm === "tenant-details" && (
              <div className="modal-overlay" role="presentation" onClick={closeActiveForm}>
                <form
                  className="card form-grid modal-card"
                  onSubmit={saveTenantDetails}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="modal-header">
                    <h3>Update Tenant Details</h3>
                    <button type="button" className="modal-close-btn" onClick={closeActiveForm} aria-label="Close">
                      ×
                    </button>
                  </div>
                  <select
                    name="tenant_id"
                    value={tenantDetailForm.tenant_id}
                    onChange={handleTenantDetailSelection}
                    required
                  >
                    <option value="">Select Tenant</option>
                    {tenants.map((tenant) => (
                      <option key={`tenant-detail-select-${tenant.id}`} value={tenant.id}>
                        {tenant.tenant_name} - {tenant.property_name}
                      </option>
                    ))}
                  </select>
                  <input
                    name="tenant_email"
                    type="email"
                    placeholder="Tenant Email"
                    value={tenantDetailForm.tenant_email}
                    onChange={handleTenantDetailInput}
                  />
                  <input
                    name="tenant_contact_no"
                    placeholder="Tenant Contact No"
                    value={tenantDetailForm.tenant_contact_no}
                    onChange={handleTenantDetailInput}
                  />
                  <input
                    name="tenant_address"
                    placeholder="Tenant Address"
                    value={tenantDetailForm.tenant_address}
                    onChange={handleTenantDetailInput}
                  />
                  <p className="rent-mode-hint full-width-label">Security deposit</p>
                  <input
                    name="advance_paid"
                    type="text"
                    inputMode="decimal"
                    placeholder="Deposit amount (e.g. 500000 or 5,00,000)"
                    value={tenantDetailForm.advance_paid}
                    onChange={handleTenantDetailInput}
                  />
                  <input
                    name="advance_paid_date"
                    type="text"
                    placeholder="Deposit date(s) — MM/DD/YYYY, MM/DD/YYYY"
                    value={tenantDetailForm.advance_paid_date}
                    onChange={handleTenantDetailInput}
                  />
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      Save Tenant Details
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeActiveForm}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
            {groupedPropertyData.length === 0 && <p>No properties added yet.</p>}
            {groupedPropertyData.map((property) => (
              <div key={`tenant-detail-${property.id}`} className="doc-property-block">
                <h4>{property.property_name}</h4>
                <div className="table-card">
                  <table className="rental-table tenant-details-table responsive-data-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Email</th>
                        <th>Contact No</th>
                        <th>Address</th>
                        <th>Deposit</th>
                        <th>Deposit Date(s)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {property.tenants.length === 0 && (
                        <tr>
                          <td colSpan="6" className="cell-span-all">No tenants for this property.</td>
                        </tr>
                      )}
                      {property.tenants.map((tenant) => (
                        <tr key={`tenant-contact-${tenant.id}`}>
                          <td data-label="Tenant">{tenant.tenant_name}</td>
                          <td data-label="Email">{tenant.tenant_email || "-"}</td>
                          <td data-label="Contact No">{tenant.tenant_contact_no || "-"}</td>
                          <td data-label="Address">{tenant.tenant_address || "-"}</td>
                          <td data-label="Deposit">
                            {Number(tenant.advance_paid || 0) > 0 ? formatInr(tenant.advance_paid) : "-"}
                          </td>
                          <td data-label="Deposit Date(s)">
                            {formatDisplayDateList(tenant.advance_paid_date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
          )
        )}

        {activeSection === "complete-summary" && (
          <div className="card complete-summary-panel">
            <div className="complete-summary-toolbar">
              <div>
                <h3>Complete Summary — All Tenants</h3>
                <p className="complete-summary-hint">
                  FY {completeSummary.fiscal_year || fyMonthColumns.fiscalYear} — all entities in one sheet.
                  Click <strong>Edit</strong> on a row to enter data; <strong>Save</strong> commits to Excel-style
                  cells. Click month total to mark correct (green) or incorrect (red).
                </p>
              </div>
              <button
                type="button"
                className="cs-export-btn"
                disabled={
                  completeSummary.months.length === 0 && fyMonthColumns.months.length === 0
                }
                onClick={handleCompleteSummaryExport}
              >
                Export to Excel
              </button>
            </div>
            {(completeSummary.months.length === 0 && fyMonthColumns.months.length === 0) && (
              <p>No FY months yet (starts April).</p>
            )}
            {(completeSummary.months.length > 0 || fyMonthColumns.months.length > 0) && (
              <div className="complete-summary-scroll">
                <p className="table-scroll-hint">Scroll horizontally to see all months →</p>
                <table className="complete-summary-table cs-excel-sheet">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="cs-sticky cs-col-entity">
                        Entity
                      </th>
                      <th rowSpan={2} className="cs-sticky cs-col-property">
                        Property
                      </th>
                      <th rowSpan={2} className="cs-sticky cs-col-tenant">
                        Tenant
                      </th>
                      <th rowSpan={2} className="cs-sticky cs-col-actions">
                        Edit
                      </th>
                      <th rowSpan={2} className="cs-spacer-col" aria-hidden="true" />
                      {(completeSummary.months.length ? completeSummary.months : fyMonthColumns.months).map(
                        (monthKey) => (
                          <th key={`hdr-${monthKey}`} colSpan={3} className="cs-month-group-head">
                            {monthKey}
                          </th>
                        )
                      )}
                    </tr>
                    <tr>
                      {(completeSummary.months.length ? completeSummary.months : fyMonthColumns.months).map(
                        (monthKey) => (
                          <React.Fragment key={`subhdr-${monthKey}`}>
                            <th className="cs-subhead">Date</th>
                            <th className="cs-subhead cs-amt-head">Amount</th>
                            <th className="cs-subhead cs-total-head">Total</th>
                          </React.Fragment>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {completeSummary.rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={
                            5 +
                            (completeSummary.months.length || fyMonthColumns.months.length) * 3
                          }
                          className="cell-span-all"
                        >
                          No tenants yet. Add tenants in Financials first.
                        </td>
                      </tr>
                    )}
                    {completeSummary.rows.map((row) => {
                      const isEditing = csEditTenantId === row.tenant_id;
                      const monthKeys = completeSummary.months.length
                        ? completeSummary.months
                        : fyMonthColumns.months;

                      return (
                        <tr key={`cs-row-${row.tenant_id}`} className={isEditing ? "cs-row-editing" : ""}>
                          <td className="cs-sticky cs-col-entity cs-excel-cell">
                            <span className="cs-cell-value">{row.entity_name}</span>
                          </td>
                          <td className="cs-sticky cs-col-property cs-excel-cell">
                            <span className="cs-cell-value">{row.property_name}</span>
                          </td>
                          <td className="cs-sticky cs-col-tenant cs-excel-cell">
                            <span className="cs-cell-value">{row.tenant_name}</span>
                          </td>
                          <td className="cs-sticky cs-col-actions cs-excel-cell cs-actions-cell">
                            {isEditing ? (
                              <div className="cs-action-btns">
                                <button
                                  type="button"
                                  className="cs-mini-btn"
                                  onClick={() => saveCsEdit(row.tenant_id)}
                                >
                                  Save
                                </button>
                                <button type="button" className="cs-mini-btn cs-mini-muted" onClick={cancelCsEdit}>
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="cs-edit-btn"
                                disabled={csEditTenantId !== null}
                                onClick={() => startCsEdit(row)}
                              >
                                Edit
                              </button>
                            )}
                          </td>
                          <td className="cs-spacer-col" aria-hidden="true" />
                          {monthKeys.map((monthKey) => {
                            const monthData = row.months?.[monthKey] || {
                              transactions: [],
                              total: 0,
                              highlight: null
                            };
                            const draftTxns = csEditDraft?.months?.[monthKey] || [];
                            const displayTxns = isEditing ? draftTxns : monthData.transactions;
                            const displayTotal = isEditing
                              ? csDraftMonthTotal(monthKey)
                              : monthData.total;

                            return (
                              <React.Fragment key={`${row.tenant_id}-${monthKey}`}>
                                <td className="cs-excel-cell cs-excel-date-col">
                                  {isEditing ? (
                                    <div className="cs-edit-stack">
                                      {draftTxns.map((txn, idx) => (
                                        <input
                                          key={`d-${monthKey}-${idx}`}
                                          className="cs-edit-input"
                                          type="text"
                                          placeholder="Date"
                                          value={txn.transaction_date}
                                          onChange={(e) =>
                                            updateCsDraftTxn(monthKey, idx, "transaction_date", e.target.value)
                                          }
                                        />
                                      ))}
                                      <button
                                        type="button"
                                        className="cs-add-btn"
                                        onClick={() => addCsDraftTxn(monthKey)}
                                      >
                                        + Line
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="cs-excel-stack">
                                      {(displayTxns.length ? displayTxns : [null]).map((txn, idx) => (
                                        <div key={`v-date-${monthKey}-${idx}`} className="cs-excel-line">
                                          {txn ? formatCsCellText(txn.transaction_date) : "—"}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="cs-excel-cell cs-excel-amt-col">
                                  {isEditing ? (
                                    <div className="cs-edit-stack">
                                      {draftTxns.map((txn, idx) => (
                                        <div key={`a-${monthKey}-${idx}`} className="cs-edit-line">
                                          <input
                                            className="cs-edit-input cs-edit-amt"
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="Amount"
                                            value={txn.amount}
                                            onChange={(e) =>
                                              updateCsDraftTxn(monthKey, idx, "amount", e.target.value)
                                            }
                                          />
                                          <button
                                            type="button"
                                            className="cs-del-btn"
                                            onClick={() => removeCsDraftTxn(monthKey, idx)}
                                            aria-label="Remove line"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="cs-excel-stack">
                                      {(displayTxns.length ? displayTxns : [null]).map((txn, idx) => (
                                        <div
                                          key={`v-amt-${monthKey}-${idx}`}
                                          className="cs-excel-line cs-cell-amount"
                                        >
                                          {txn ? formatCsCellAmount(txn.amount) : "—"}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="cs-excel-cell cs-month-total-cell">
                                  <button
                                    type="button"
                                    className={`cs-month-total ${monthHighlightClass(monthData.highlight)}`}
                                    title="Click: mark correct (green) → incorrect (red) → clear"
                                    disabled={isEditing}
                                    onClick={() =>
                                      cycleCompleteSummaryHighlight(
                                        row.tenant_id,
                                        monthKey,
                                        monthData.highlight
                                      )
                                    }
                                  >
                                    {formatInr(displayTotal)}
                                  </button>
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </main>
    </div>
  );
}

export default App;
